import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type FormEvent, type KeyboardEvent, type SetStateAction } from 'react';
import { ApiError, api, sanitizeBackupMessage } from '../api';
import {
  createRecoveryKeyPair,
  decryptRecoveryKit,
  downloadRecoveryKit,
  encryptPasswordForController,
  readRecoveryKitFile,
} from '../backup-crypto';
import type {
  BackupArchivedIntegration,
  BackupLifecycleOperationType,
  BackupOperation,
  BackupOperationPayload,
  BackupOperationType,
  BackupOverview,
  BackupReactivateRepositoryGenerationPayload,
  BackupRecoveryKit,
  BackupRecoveryKitIdentity,
  BackupRecoveryKitPurpose,
  BackupRestoreTest,
} from '../types';
import { formatBytes, formatDuration } from '../utils';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { useActiveTabVisibility } from './useActiveTabVisibility';
import { BackupReactivationDialog } from './BackupReactivationDialog';
import { MediaArchivePanel } from './MediaArchivePanel';
import { getBranding } from '../branding';

interface BackupRecoveryPanelProps {
  imageHostingEnabled?: boolean;
  notify: (message: string, tone?: 'success' | 'error') => void;
  dashboardView?: BackupDashboardView;
  recordView?: BackupRecordView;
  onViewChange?: (dashboardView: BackupDashboardView, recordView: BackupRecordView) => void;
  onOpenVideo?: (videoId: string) => void;
  hostedMemberFiltering?: boolean;
}

type RefreshKind = 'initial' | 'manual' | 'poll';
export type BackupDashboardView = 'backup' | 'health' | 'automation' | 'storage-recovery' | 'media-archive' | 'records';
export type BackupRecordView = 'snapshots' | 'restore-tests' | 'activity';

const BACKUP_DASHBOARD_VIEWS: Array<{
  id: BackupDashboardView;
  label: string;
  description: string;
  icon: IconName;
}> = [
  { id: 'backup', label: 'Backup', description: 'Run and review backups', icon: 'upload' },
  { id: 'health', label: 'Health checks', description: 'Validate recovery readiness', icon: 'shield' },
  { id: 'automation', label: 'Automation', description: 'Schedule and retention', icon: 'settings' },
  { id: 'storage-recovery', label: 'Storage & recovery', description: 'Drive and recovery kit', icon: 'hard-drive' },
  { id: 'media-archive', label: 'R2 Media Archive', description: 'Read-only content copy', icon: 'archive' },
  { id: 'records', label: 'Records', description: 'Snapshots and history', icon: 'archive' },
];

type PendingOperation = {
  type: BackupOperationType;
  payload?: BackupOperationPayload;
  label: string;
  authorization?: boolean;
  recoveryPrivateKey?: CryptoKey;
  recoveryPurpose?: BackupRecoveryKitPurpose;
  reactivation?: {
    targetGenerationId: string;
    kit: PreparedReactivationKit;
  };
};

type LifecycleDialog = 'manage' | 'reset' | 'disconnect' | 'fresh_start' | null;
type RecoveryKey = { privateKey: CryptoKey; purpose: BackupRecoveryKitPurpose };
type AvailableRecoveryKit = { operationId: string; purpose: BackupRecoveryKitPurpose; kit: BackupRecoveryKit };
type PreparedReactivationKit = {
  provider: 'google_drive';
  googleAccountEmail: string;
  repositoryId: string;
  kitIdentity: BackupRecoveryKitIdentity;
  encryptedPassword: string;
};

const OAUTH_OPERATION_TYPES = new Set<BackupOperationType>(['connect_google', 'reauthorize_google', 'reactivate_repository_generation']);
const LIFECYCLE_OPERATION_TYPES = new Set<BackupLifecycleOperationType>([
  'reauthorize_google',
  'reset_google_before_initialization',
  'disconnect_google',
  'detach_repository_generation',
  'reactivate_repository_generation',
]);

const RETENTION_BOUNDS = {
  daily: { min: 1, max: 90 },
  weekly: { min: 1, max: 52 },
  monthly: { min: 1, max: 36 },
} as const;

const OPERATION_LABELS: Record<BackupOperationType, string> = {
  connect_google: 'Connect Google Drive',
  reauthorize_google: 'Reauthorize Google Drive',
  reset_google_before_initialization: 'Choose a different Google account',
  disconnect_google: 'Disconnect Google Drive',
  detach_repository_generation: 'Start fresh',
  reactivate_repository_generation: 'Reactivate archived integration',
  initialize_repository: 'Initialize repository',
  reconnect_repository: 'Reconnect repository',
  export_recovery_kit: 'Export recovery kit',
  update_schedule: 'Update schedule',
  update_retention: 'Update retention',
  preflight: 'Run preflight',
  backup_now: 'Back up now',
  repository_check: 'Check repository',
  list_snapshots: 'Discover snapshots',
  test_restore: 'Run restore test',
};

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof ApiError ? sanitizeBackupMessage(reason.message) ?? fallback : fallback;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return 'Not yet';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function operationLabel(type: string): string {
  return OPERATION_LABELS[type as BackupOperationType] ?? type.replace(/_/g, ' ');
}

function operationTimestamp(operation: BackupOperation): number {
  const value = operation.finishedAt ?? operation.startedAt ?? operation.createdAt;
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
}

function restoreTestTimestamp(test: BackupRestoreTest): number {
  const value = test.finishedAt ?? test.createdAt;
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
}

function findLatestOperationOfType(operations: BackupOperation[], type: BackupOperationType): BackupOperation | undefined {
  return operations.reduce<BackupOperation | undefined>((latest, operation) => {
    if (operation.type !== type) return latest;
    return !latest || operationTimestamp(operation) > operationTimestamp(latest) ? operation : latest;
  }, undefined);
}

function findLatestUnresolvedFailedOperation(operations: BackupOperation[]): BackupOperation | null {
  const latestSuccessByType = new Map<string, number>();
  for (const operation of operations) {
    if (operation.state !== 'succeeded') continue;
    const timestamp = operationTimestamp(operation);
    latestSuccessByType.set(operation.type, Math.max(latestSuccessByType.get(operation.type) ?? Number.NEGATIVE_INFINITY, timestamp));
  }

  return operations.reduce<BackupOperation | null>((latest, operation) => {
    if (operation.state !== 'failed') return latest;
    const timestamp = operationTimestamp(operation);
    if ((latestSuccessByType.get(operation.type) ?? Number.NEGATIVE_INFINITY) > timestamp) return latest;
    return !latest || timestamp > operationTimestamp(latest) ? operation : latest;
  }, null);
}

function statusTone(status: string): 'ready' | 'processing' | 'failed' | 'neutral' {
  if (['protected', 'ready', 'healthy', 'success', 'succeeded', 'completed', 'passed', 'online'].includes(status)) return 'ready';
  if (['running', 'queued', 'pending', 'authorizing', 'waiting_for_user', 'cancelling'].includes(status)) return 'processing';
  if (['failed', 'error', 'incompatible', 'offline'].includes(status)) return 'failed';
  return 'neutral';
}

function makeIdempotencyKey(type: BackupOperationType): string {
  const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `backup-${type}-${id}`;
}

function operationIntentKey(type: BackupOperationType, payload?: BackupOperationPayload): string {
  if (type !== 'reactivate_repository_generation' || !payload || !('kitIdentity' in payload)) {
    return `${type}:${JSON.stringify(payload ?? {})}`;
  }
  const reactivation = payload as BackupReactivateRepositoryGenerationPayload;
  return `${type}:${JSON.stringify({
    targetGenerationId: reactivation.targetGenerationId,
    kitIdentity: reactivation.kitIdentity,
    confirmation: reactivation.confirmation,
  })}`;
}

function timezoneIsValid(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function supportedTimezones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  return intl.supportedValuesOf?.('timeZone') ?? ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Australia/Sydney'];
}

function openAuthorizationWindow(): Window | null {
  const authorizationWindow = window.open('about:blank', 'streamline-google-drive');
  if (authorizationWindow) {
    authorizationWindow.opener = null;
    authorizationWindow.document.title = 'Opening Google Drive…';
    authorizationWindow.document.body.textContent = 'Waiting for a secure Google Drive authorization link…';
  }
  return authorizationWindow;
}

function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
  const tablist = event.currentTarget.closest('[role="tablist"]');
  const tabs = Array.from(tablist?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
  const currentIndex = tabs.indexOf(event.currentTarget);
  let nextIndex: number | undefined;

  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
  if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  if (event.key === 'Home') nextIndex = 0;
  if (event.key === 'End') nextIndex = tabs.length - 1;
  const nextTab = nextIndex === undefined ? undefined : tabs[nextIndex];
  if (!nextTab) return;

  event.preventDefault();
  nextTab.focus();
  nextTab.click();
}

export function BackupRecoveryPanel({
  notify,
  dashboardView,
  recordView,
  onViewChange,
  onOpenVideo,
  hostedMemberFiltering,
  imageHostingEnabled,
}: BackupRecoveryPanelProps) {
  const branding = getBranding();
  const [overview, setOverview] = useState<BackupOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [pendingOperation, setPendingOperation] = useState<PendingOperation | null>(null);
  const [password, setPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthenticating, setReauthenticating] = useState(false);
  const [googleAuthorizationUrl, setGoogleAuthorizationUrl] = useState('');
  const [destinationFolderName, setDestinationFolderName] = useState('Streamline-Backups/Streamline');
  const [initializationConfirmation, setInitializationConfirmation] = useState('');
  const [importedRecoveryKit, setImportedRecoveryKit] = useState<BackupRecoveryKit | null>(null);
  const [availableRecoveryKit, setAvailableRecoveryKit] = useState<AvailableRecoveryKit | null>(null);
  const [downloadedRecoveryOperationId, setDownloadedRecoveryOperationId] = useState('');
  const [freshStartAcknowledgedOperationId, setFreshStartAcknowledgedOperationId] = useState('');
  const [freshStartExportOperationId, setFreshStartExportOperationId] = useState('');
  const [integrationSwitchAcknowledgedOperationId, setIntegrationSwitchAcknowledgedOperationId] = useState('');
  const [integrationSwitchExportOperationId, setIntegrationSwitchExportOperationId] = useState('');
  const [selectedArchivedIntegration, setSelectedArchivedIntegration] = useState<BackupArchivedIntegration | null>(null);
  const [reactivationRecoveryKit, setReactivationRecoveryKit] = useState<PreparedReactivationKit | null>(null);
  const [reactivationConfirmation, setReactivationConfirmation] = useState('');
  const [reactivationError, setReactivationError] = useState('');
  const [processingRecoveryResult, setProcessingRecoveryResult] = useState(false);
  const [processingReactivationKit, setProcessingReactivationKit] = useState(false);
  const [lifecycleDialog, setLifecycleDialog] = useState<LifecycleDialog>(null);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState('');
  const [freshStartConfirmation, setFreshStartConfirmation] = useState('');
  const [schedule, setSchedule] = useState({ time: '03:00', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' });
  const [retention, setRetention] = useState({ daily: 7, weekly: 4, monthly: 6 });
  const [internalDashboardView, setInternalDashboardView] = useState<BackupDashboardView>('backup');
  const [internalRecordView, setInternalRecordView] = useState<BackupRecordView>('snapshots');
  const selectedDashboardView = dashboardView ?? internalDashboardView;
  const selectedRecordView = recordView ?? internalRecordView;
  const dashboardTabRail = useActiveTabVisibility(selectedDashboardView);
  const recordTabRail = useActiveTabVisibility(selectedRecordView);
  const formsInitializedRef = useRef(false);
  const authorizationWindowRef = useRef<Window | null>(null);
  const authorizationOperationIdRef = useRef('');
  const idempotencyKeysRef = useRef(new Map<string, string>());
  const recoveryPrivateKeysRef = useRef(new Map<string, RecoveryKey>());
  const consumedRecoveryResultsRef = useRef(new Set<string>());
  const currentGenerationIdRef = useRef<string | null | undefined>(undefined);
  const recoveryResultEpochRef = useRef(0);
  const recoveryImportEpochRef = useRef(0);
  const overviewAppliedRef = useRef(false);
  const onViewChangeRef = useRef(onViewChange);
  const reactivationDialogTargetRef = useRef<BackupArchivedIntegration | null>(null);
  onViewChangeRef.current = onViewChange;

  const applyOverview = useCallback((next: BackupOverview) => {
    setOverview(next);
    const activeAuthorization = next.activeOperation && OAUTH_OPERATION_TYPES.has(next.activeOperation.type as BackupOperationType)
      ? next.activeOperation
      : null;
    const trackedAuthorization = authorizationOperationIdRef.current
      ? [next.activeOperation, ...next.history].find((operation) => operation?.id === authorizationOperationIdRef.current)
      : undefined;
    const authorizationCancelling = activeAuthorization?.type === 'reactivate_repository_generation' &&
      activeAuthorization.transitionSubstate === 'cancellation_requested';
    if (activeAuthorization && !authorizationCancelling) {
      authorizationOperationIdRef.current = activeAuthorization.id;
      if (activeAuthorization.authorizationUrl) setGoogleAuthorizationUrl(activeAuthorization.authorizationUrl);
    }
    const trackedAuthorizationActive = trackedAuthorization && !['succeeded', 'failed', 'cancelled'].includes(trackedAuthorization.state);
    if (!activeAuthorization && !trackedAuthorizationActive && !authorizationOperationIdRef.current) {
      setGoogleAuthorizationUrl('');
    }
    if (authorizationCancelling || (trackedAuthorization && ['succeeded', 'failed', 'cancelled'].includes(trackedAuthorization.state))) {
      setGoogleAuthorizationUrl('');
      authorizationOperationIdRef.current = '';
      if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) authorizationWindowRef.current.close();
      authorizationWindowRef.current = null;
    }
    const nextGenerationId = next.currentGeneration?.id ?? null;
    if (overviewAppliedRef.current && currentGenerationIdRef.current !== nextGenerationId) {
      recoveryResultEpochRef.current += 1;
      recoveryImportEpochRef.current += 1;
      recoveryPrivateKeysRef.current.clear();
      consumedRecoveryResultsRef.current.clear();
      idempotencyKeysRef.current.clear();
      setAvailableRecoveryKit(null);
      setImportedRecoveryKit(null);
      setReactivationRecoveryKit(null);
      setSelectedArchivedIntegration(null);
      setReactivationConfirmation('');
      setReactivationError('');
      setProcessingReactivationKit(false);
      setDownloadedRecoveryOperationId('');
      setFreshStartAcknowledgedOperationId('');
      setFreshStartExportOperationId('');
      setIntegrationSwitchAcknowledgedOperationId('');
      setIntegrationSwitchExportOperationId('');
      reactivationDialogTargetRef.current = null;
      setFreshStartConfirmation('');
      setPendingOperation(null);
      setPassword('');
      setReauthError('');
      setGoogleAuthorizationUrl('');
      authorizationOperationIdRef.current = '';
      if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) authorizationWindowRef.current.close();
      authorizationWindowRef.current = null;
      setLifecycleDialog(null);
      setBusyAction('');
      formsInitializedRef.current = false;
      setInternalDashboardView('backup');
      setInternalRecordView('snapshots');
      onViewChangeRef.current?.('backup', 'snapshots');
    }
    overviewAppliedRef.current = true;
    currentGenerationIdRef.current = nextGenerationId;
    if (!formsInitializedRef.current) {
      setDestinationFolderName(next.config.google.folderName || 'Streamline-Backups/Streamline');
      setSchedule({ time: next.config.schedule.time, timezone: next.config.schedule.timezone });
      setRetention({
        daily: next.config.retention.daily,
        weekly: next.config.retention.weekly,
        monthly: next.config.retention.monthly,
      });
      formsInitializedRef.current = true;
    }
  }, []);

  const loadOverview = useCallback(async (kind: RefreshKind = 'manual'): Promise<BackupOverview | null> => {
    if (kind === 'initial') setLoading(true);
    if (kind === 'manual') setRefreshing(true);
    if (kind !== 'poll') setLoadError('');
    try {
      const next = await api.backupOverview();
      applyOverview(next);
      setLoadError('');
      return next;
    } catch (reason) {
      if (kind !== 'poll') setLoadError(errorMessage(reason, 'Unable to load Backup & Recovery.'));
      return null;
    } finally {
      if (kind === 'initial') setLoading(false);
      if (kind === 'manual') setRefreshing(false);
    }
  }, [applyOverview]);

  useEffect(() => {
    void loadOverview('initial');
    const timer = window.setInterval(() => void loadOverview('poll'), 30_000);
    return () => window.clearInterval(timer);
  }, [loadOverview]);

  const activeOperationId = overview?.activeOperation?.id;
  useEffect(() => {
    if (!activeOperationId) return;
    let active = true;
    let timer = 0;
    const poll = async () => {
      await loadOverview('poll');
      if (active) timer = window.setTimeout(() => void poll(), 3_000);
    };
    timer = window.setTimeout(() => void poll(), 3_000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [activeOperationId, loadOverview]);

  useEffect(() => {
    if (!googleAuthorizationUrl || !authorizationWindowRef.current || authorizationWindowRef.current.closed) return;
    authorizationWindowRef.current.location.href = googleAuthorizationUrl;
  }, [googleAuthorizationUrl]);

  useEffect(() => () => {
    recoveryResultEpochRef.current += 1;
    recoveryImportEpochRef.current += 1;
    recoveryPrivateKeysRef.current.clear();
    consumedRecoveryResultsRef.current.clear();
    if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) authorizationWindowRef.current.close();
  }, []);

  useEffect(() => {
    const operations = [overview?.activeOperation, ...(overview?.history ?? [])].filter(Boolean);
    const recoverable = operations.find((operation) => operation?.recoveryKit && recoveryPrivateKeysRef.current.has(operation.id));
    if (!recoverable?.recoveryKit || consumedRecoveryResultsRef.current.has(recoverable.id)) return;
    consumedRecoveryResultsRef.current.add(recoverable.id);
    const recoveryKey = recoveryPrivateKeysRef.current.get(recoverable.id);
    if (!recoveryKey) return;
    const workEpoch = recoveryResultEpochRef.current;
    setProcessingRecoveryResult(true);
    void decryptRecoveryKit(recoverable.recoveryKit, recoveryKey.privateKey)
      .then((kit) => {
        if (workEpoch !== recoveryResultEpochRef.current) return;
        recoveryPrivateKeysRef.current.delete(recoverable.id);
        setImportedRecoveryKit(null);
        setAvailableRecoveryKit({ operationId: recoverable.id, purpose: recoveryKey.purpose, kit });
        notify('Recovery kit is ready. Download and store it outside this server.', 'success');
      })
      .catch(() => {
        if (workEpoch !== recoveryResultEpochRef.current) return;
        recoveryPrivateKeysRef.current.delete(recoverable.id);
        if (recoveryKey.purpose === 'fresh_start') {
          setFreshStartExportOperationId('');
          setFreshStartAcknowledgedOperationId('');
          setDownloadedRecoveryOperationId('');
        }
        setActionError('The encrypted recovery kit could not be opened in this browser. Export a new copy and try again.');
      })
      .finally(() => {
        if (workEpoch === recoveryResultEpochRef.current) setProcessingRecoveryResult(false);
      });
  }, [notify, overview]);

  useEffect(() => {
    if (overview && !overview.integrationSwitchProofAcknowledged && integrationSwitchAcknowledgedOperationId) {
      recoveryPrivateKeysRef.current.delete(integrationSwitchExportOperationId);
      consumedRecoveryResultsRef.current.delete(integrationSwitchExportOperationId);
      setIntegrationSwitchAcknowledgedOperationId('');
      setIntegrationSwitchExportOperationId('');
      setDownloadedRecoveryOperationId('');
      if (availableRecoveryKit?.purpose === 'integration_switch') setAvailableRecoveryKit(null);
    }
  }, [availableRecoveryKit?.purpose, integrationSwitchAcknowledgedOperationId, integrationSwitchExportOperationId, overview]);

  useEffect(() => {
    const exportOperationId = freshStartExportOperationId || integrationSwitchExportOperationId;
    if (!exportOperationId) return;
    const operation = [overview?.activeOperation, ...(overview?.history ?? [])]
      .find((candidate) => candidate?.id === exportOperationId);
    if (!operation || !['failed', 'cancelled'].includes(operation.state)) return;
    const purpose = exportOperationId === freshStartExportOperationId ? 'fresh_start' : 'integration_switch';
    recoveryPrivateKeysRef.current.delete(exportOperationId);
    consumedRecoveryResultsRef.current.delete(exportOperationId);
    setDownloadedRecoveryOperationId('');
    if (purpose === 'fresh_start') {
      setFreshStartExportOperationId('');
      setFreshStartAcknowledgedOperationId('');
    } else {
      setIntegrationSwitchExportOperationId('');
      setIntegrationSwitchAcknowledgedOperationId('');
    }
    if (availableRecoveryKit?.purpose === purpose) setAvailableRecoveryKit(null);
    setActionError(`The ${purpose === 'fresh_start' ? 'fresh-start' : 'integration-switch'} recovery-kit export did not complete. Export a new copy before continuing.`);
  }, [availableRecoveryKit?.purpose, freshStartExportOperationId, integrationSwitchExportOperationId, overview]);

  const setupStage = useMemo(() => {
    if (!overview?.controller.ready || overview.controller.compatible === false) return 1;
    if (overview.setupComplete || (overview.config.repositoryInitialized && overview.connectionStatus === 'disconnected')) return 5;
    if (!overview.config.google.connected) return 2;
    if (!overview.config.repositoryInitialized || !overview.config.recoveryKitAcknowledged) return 3;
    if (!overview.config.schedule.configured || !overview.config.retention.configured) return 4;
    return 5;
  }, [overview]);

  const setupComplete = Boolean(overview?.setupComplete || (overview?.config.repositoryInitialized && overview.connectionStatus === 'disconnected') || setupStage === 5);
  const activeOperation = overview?.activeOperation ?? null;
  const latestFailedOperation = activeOperation
    ? null
    : findLatestUnresolvedFailedOperation(overview?.history ?? []);
  const displayedOperation = activeOperation ?? latestFailedOperation;
  const displayedOperationTimestamp = displayedOperation?.finishedAt ?? displayedOperation?.startedAt ?? displayedOperation?.createdAt;
  const controllerUsable = Boolean(overview?.controller.ready && overview.controller.compatible !== false);
  const actionDisabled = Boolean(activeOperation || busyAction || !controllerUsable);
  const repositoryConnectedReady = Boolean(
    overview?.config.google.connected && overview.connectionStatus === 'connected' &&
    overview.config.repositoryInitialized && overview.repositoryStatus === 'ready',
  );
  const lifecycleWorkActive = Boolean(
    (activeOperation && LIFECYCLE_OPERATION_TYPES.has(activeOperation.type as BackupLifecycleOperationType)) ||
        (busyAction && LIFECYCLE_OPERATION_TYPES.has(busyAction as BackupLifecycleOperationType)),
  );
  const routineActionsDisabled = actionDisabled || !repositoryConnectedReady || lifecycleWorkActive;
  const backupNowDisabled = actionDisabled || !overview?.backupNowAllowed || lifecycleWorkActive;
  const sourceHasRepository = Boolean(
    overview?.config.repositoryInitialized && (overview.currentGeneration?.repositoryId || overview.config.repositoryId),
  );
  const integrationSwitchProofConfirmed = Boolean(
    !sourceHasRepository || (
      integrationSwitchExportOperationId &&
      integrationSwitchAcknowledgedOperationId === integrationSwitchExportOperationId &&
      overview?.integrationSwitchProofAcknowledged
    ),
  );
  const activationReviewPending = Boolean(
    overview?.currentActivation && (!overview.currentActivation.scheduleReviewed || !overview.currentActivation.retentionReviewed),
  );
  const sourceReactivationEligible = Boolean(
    overview && overview.repositoryStatus !== 'initializing' && overview.connectionStatus !== 'attention_required' &&
    (!overview.config.repositoryInitialized || Boolean(overview.currentGeneration?.repositoryId || overview.config.repositoryId)) &&
    (overview.config.repositoryInitialized || !overview.config.google.connected),
  );
  const sourceReactivationBlockedReason = activeOperation
    ? 'Wait for the active backup operation to finish.'
    : !controllerUsable
      ? 'The backup controller must be online and compatible.'
      : overview?.repositoryStatus === 'initializing'
        ? 'Wait for repository initialization to finish.'
        : overview?.connectionStatus === 'attention_required'
          ? 'Reauthorize or disconnect the current Google Drive integration first.'
          : overview?.config.repositoryInitialized && !overview.currentGeneration?.repositoryId && !overview.config.repositoryId
            ? 'The current repository identity is incomplete.'
            : overview?.config.google.connected && !overview.config.repositoryInitialized
              ? 'Reset the uninitialized Google connection before reactivation.'
              : overview?.lifecycleActions.reactivate_repository_generation.blockedReason;
  const canBeginFreshStart = Boolean(
    overview?.config.repositoryInitialized && overview.repositoryStatus === 'ready' && overview.config.repositoryId &&
    overview.config.google.accountId && overview.config.google.folderId && controllerUsable && !activeOperation && !busyAction,
  );
  const routineBlockedReason = activeOperation
    ? 'Wait for the active backup operation to finish or be cancelled.'
    : !overview?.config.repositoryInitialized || overview.repositoryStatus !== 'ready'
      ? 'An initialized repository is required.'
      : !overview.config.google.connected || overview.connectionStatus !== 'connected'
        ? 'Google Drive is disconnected. Reauthorize the pinned account and folder to resume backups and repository actions.'
        : !controllerUsable
          ? 'The backup controller must be online and compatible.'
          : lifecycleWorkActive
            ? 'Wait for the Google account lifecycle operation to finish.'
            : undefined;

  async function executeOperation(operation: PendingOperation): Promise<BackupOperation | null> {
    const keyName = operationIntentKey(operation.type, operation.payload);
    const idempotencyKey = idempotencyKeysRef.current.get(keyName) ?? makeIdempotencyKey(operation.type);
    idempotencyKeysRef.current.set(keyName, idempotencyKey);
    setBusyAction(operation.type);
    setActionError('');
    try {
      const created = await api.createBackupOperation(operation.type, operation.payload, idempotencyKey);
      idempotencyKeysRef.current.delete(keyName);
      if (operation.recoveryPrivateKey) {
        recoveryPrivateKeysRef.current.set(created.id, {
          privateKey: operation.recoveryPrivateKey,
          purpose: operation.recoveryPurpose ?? 'manual',
        });
        if (operation.recoveryPurpose === 'fresh_start') setFreshStartExportOperationId(created.id);
        if (operation.recoveryPurpose === 'integration_switch') setIntegrationSwitchExportOperationId(created.id);
      }
      if (operation.authorization) authorizationOperationIdRef.current = created.id;
      if (created.authorizationUrl) {
        setGoogleAuthorizationUrl(created.authorizationUrl);
        if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) {
          authorizationWindowRef.current.location.href = created.authorizationUrl;
        }
      } else if (operation.authorization && authorizationWindowRef.current) {
        authorizationWindowRef.current.document.body.textContent = `Authorization is being prepared. Return to ${branding.productName} if this window does not redirect shortly.`;
      }
      notify(`${operation.label} queued. You can leave this page while the controller works.`);
      await loadOverview('poll');
      return created;
    } catch (reason) {
      if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) authorizationWindowRef.current.close();
      authorizationWindowRef.current = null;
      authorizationOperationIdRef.current = '';
      setActionError(errorMessage(reason, `Unable to start ${operation.label.toLowerCase()}.`));
      return null;
    } finally {
      setBusyAction('');
    }
  }

  function requestOperation(operation: PendingOperation, requireRecentAuthentication = false) {
    if (actionDisabled) return;
    setActionError('');
    if (requireRecentAuthentication) {
      setLifecycleDialog(null);
      setPassword('');
      setReauthError('');
      setPendingOperation(operation);
      return;
    }
    if (operation.authorization) authorizationWindowRef.current = openAuthorizationWindow();
    void executeOperation(operation);
  }

  function selectDashboardView(nextDashboardView: BackupDashboardView) {
    if (dashboardView === undefined) setInternalDashboardView(nextDashboardView);
    onViewChange?.(nextDashboardView, selectedRecordView);
  }

  function selectRecordView(nextRecordView: BackupRecordView) {
    if (dashboardView === undefined) setInternalDashboardView('records');
    if (recordView === undefined) setInternalRecordView(nextRecordView);
    onViewChange?.('records', nextRecordView);
  }

  async function submitReauthentication(event: FormEvent) {
    event.preventDefault();
    if (!pendingOperation || !password) return;
    if (pendingOperation.authorization) authorizationWindowRef.current = openAuthorizationWindow();
    setReauthenticating(true);
    setActionError('');
    setReauthError('');
    try {
      await api.reauthenticateBackups(password);
      const operation = pendingOperation;
      setPendingOperation(null);
      setPassword('');
      setReauthError('');
      const created = await executeOperation(operation);
      if (created && operation.type === 'export_recovery_kit' && operation.recoveryPurpose === 'fresh_start') {
        setLifecycleDialog('fresh_start');
      }
      if (operation.type === 'export_recovery_kit' && operation.recoveryPurpose === 'integration_switch') {
        setSelectedArchivedIntegration(reactivationDialogTargetRef.current);
        reactivationDialogTargetRef.current = null;
      }
      if (operation.type === 'reactivate_repository_generation') {
        if (created) {
          setSelectedArchivedIntegration(null);
          setReactivationConfirmation('');
          setReactivationRecoveryKit(null);
        } else if (operation.reactivation) {
          const target = overview?.archivedIntegrations.find((integration) => integration.id === operation.reactivation?.targetGenerationId) ?? null;
          setSelectedArchivedIntegration(target);
          setReactivationRecoveryKit(target ? operation.reactivation.kit : null);
          setReactivationConfirmation(target ? 'REACTIVATE' : '');
        }
      }
    } catch (reason) {
      if (authorizationWindowRef.current && !authorizationWindowRef.current.closed) authorizationWindowRef.current.close();
      authorizationWindowRef.current = null;
      setReauthError(reason instanceof Error && !(reason instanceof ApiError)
        ? reason.message
        : errorMessage(reason, 'Unable to confirm your password.'));
    } finally {
      setPassword('');
      setReauthenticating(false);
    }
  }

  function reauthorizeGoogle() {
    if (!overview?.lifecycleActions.reauthorize_google.available) return;
    requestOperation({
      type: 'reauthorize_google',
      payload: {},
      label: 'Google Drive reauthorization',
      authorization: true,
    }, true);
  }

  function resetGoogleBeforeInitialization() {
    if (!overview?.lifecycleActions.reset_google_before_initialization.available) return;
    requestOperation({
      type: 'reset_google_before_initialization',
      payload: {},
      label: 'Choosing a different Google account',
    }, true);
  }

  function disconnectGoogle() {
    if (disconnectConfirmation !== 'DISCONNECT' || !overview?.lifecycleActions.disconnect_google.available) return;
    requestOperation({
      type: 'disconnect_google',
      payload: { confirmation: 'DISCONNECT' },
      label: 'Google Drive disconnection',
    }, true);
  }

  function detachRepositoryGeneration() {
    if (freshStartConfirmation !== 'START FRESH' ||
        freshStartAcknowledgedOperationId !== freshStartExportOperationId ||
        !overview?.freshStartProofAcknowledged ||
        !overview.lifecycleActions.detach_repository_generation.available) return;
    requestOperation({
      type: 'detach_repository_generation',
      payload: { confirmation: 'START FRESH' },
      label: 'Starting fresh',
    }, true);
  }

  function connectGoogle() {
    const folderName = destinationFolderName.trim();
    if (!folderName) {
      setActionError('Enter a dedicated Google Drive folder name.');
      return;
    }
    requestOperation({
      type: 'connect_google',
      payload: {
        destinationFolderName: folderName,
        ...(importedRecoveryKit ? {
          expectedAccountId: importedRecoveryKit.googleAccountId,
          expectedFolderId: importedRecoveryKit.googleDriveFolderId,
        } : overview?.config.google.accountId && overview.config.google.folderId ? {
          expectedAccountId: overview.config.google.accountId,
          expectedFolderId: overview.config.google.folderId,
        } : {}),
      },
      label: 'Google Drive connection',
      authorization: true,
    }, true);
  }

  async function initializeRepository() {
    if (initializationConfirmation !== 'INITIALIZE' || !overview?.controller.publicKey) return;
    setBusyAction('preparing_recovery_key');
    setActionError('');
    try {
      const keys = await createRecoveryKeyPair();
      requestOperation({
        type: 'initialize_repository',
        payload: { repositoryMode: 'new', confirmation: 'INITIALIZE', browserPublicKey: keys.publicKey },
        label: 'Repository initialization',
        recoveryPrivateKey: keys.privateKey,
      }, true);
    } catch {
      setActionError('This browser could not create the temporary recovery encryption key.');
    } finally {
      setBusyAction('');
    }
  }

  async function exportRecoveryKit(purpose: BackupRecoveryKitPurpose = 'manual') {
    if (!overview?.controller.publicKey) return;
    setBusyAction('preparing_recovery_key');
    setActionError('');
    if (purpose === 'integration_switch') {
      setIntegrationSwitchExportOperationId('');
      setIntegrationSwitchAcknowledgedOperationId('');
      setDownloadedRecoveryOperationId('');
      if (availableRecoveryKit?.purpose === 'integration_switch') setAvailableRecoveryKit(null);
    }
    try {
      const keys = await createRecoveryKeyPair();
      if (purpose === 'integration_switch') {
        reactivationDialogTargetRef.current = selectedArchivedIntegration;
        setSelectedArchivedIntegration(null);
      }
      requestOperation({
        type: 'export_recovery_kit',
        payload: { browserPublicKey: keys.publicKey, ...(purpose !== 'manual' ? { purpose } : {}) },
        label: purpose === 'fresh_start' ? 'Fresh-start recovery-kit export' : purpose === 'integration_switch' ? 'Integration-switch recovery-kit export' : 'Recovery-kit export',
        recoveryPrivateKey: keys.privateKey,
        recoveryPurpose: purpose,
      }, true);
    } catch {
      if (purpose === 'integration_switch') {
        setSelectedArchivedIntegration(reactivationDialogTargetRef.current);
        reactivationDialogTargetRef.current = null;
      }
      setActionError('This browser could not create the temporary recovery encryption key.');
    } finally {
      setBusyAction('');
    }
  }

  async function reconnectRepository() {
    if (!importedRecoveryKit || !overview?.controller.publicKey) return;
    if (overview.config.google.accountId !== importedRecoveryKit.googleAccountId ||
        overview.config.google.folderId !== importedRecoveryKit.googleDriveFolderId) {
      setActionError('Reconnect Google Drive to the account and folder recorded in this recovery kit first.');
      return;
    }
    setBusyAction('preparing_recovery_key');
    setActionError('');
    try {
      const [keys, encryptedPassword] = await Promise.all([
        createRecoveryKeyPair(),
        encryptPasswordForController(importedRecoveryKit.resticPassword, overview.controller.publicKey),
      ]);
      requestOperation({
        type: 'reconnect_repository',
        payload: {
          expectedRepositoryId: importedRecoveryKit.repositoryId,
          expectedAccountId: importedRecoveryKit.googleAccountId,
          expectedFolderId: importedRecoveryKit.googleDriveFolderId,
          encryptedPassword,
          browserPublicKey: keys.publicKey,
        },
        label: 'Repository reconnect',
        recoveryPrivateKey: keys.privateKey,
      }, true);
    } catch {
      setActionError('The recovery kit could not be encrypted for this controller.');
    } finally {
      setBusyAction('');
    }
  }

  async function importRecoveryKit(file: File | undefined) {
    if (!file) return;
    setActionError('');
    try {
      const kit = await readRecoveryKitFile(file);
      setImportedRecoveryKit(kit);
      setDestinationFolderName(kit.googleDriveFolderName);
      notify('Recovery kit loaded in this browser. Its password has not been uploaded.', 'success');
    } catch (reason) {
      setImportedRecoveryKit(null);
      setActionError(reason instanceof Error ? reason.message : 'Unable to read the recovery kit.');
    }
  }

  async function importReactivationRecoveryKit(file: File | undefined) {
    if (!file || !selectedArchivedIntegration) return;
    const integration = selectedArchivedIntegration;
    const controllerPublicKey = overview?.controller.publicKey;
    if (!controllerPublicKey) {
      setReactivationError('The backup controller encryption key is unavailable. Refresh and try again.');
      return;
    }
    const workEpoch = ++recoveryImportEpochRef.current;
    setActionError('');
    setReactivationError('');
    setReactivationRecoveryKit(null);
    setReactivationConfirmation('');
    setProcessingReactivationKit(true);
    try {
      const kit = await readRecoveryKitFile(file);
      if (workEpoch !== recoveryImportEpochRef.current) return;
      if (kit.provider !== integration.provider || !kit.repositoryId.startsWith(integration.repositoryIdPrefix)) {
        throw new Error('This recovery kit does not match the selected archived integration.');
      }
      const encryptedPassword = await encryptPasswordForController(kit.resticPassword, controllerPublicKey);
      if (workEpoch !== recoveryImportEpochRef.current) return;
      setReactivationRecoveryKit({
        provider: kit.provider,
        googleAccountEmail: kit.googleAccountEmail,
        repositoryId: kit.repositoryId,
        kitIdentity: {
          googleAccountId: kit.googleAccountId,
          googleDriveFolderId: kit.googleDriveFolderId,
          repositoryId: kit.repositoryId,
        },
        encryptedPassword,
      });
      notify('Recovery-kit schema and repository summary passed the browser precheck. Exact stable identity and password validation happens on the server.', 'success');
    } catch (reason) {
      if (workEpoch !== recoveryImportEpochRef.current) return;
      setReactivationError(reason instanceof Error ? reason.message : 'Unable to prepare the archived recovery kit.');
    } finally {
      if (workEpoch === recoveryImportEpochRef.current) setProcessingReactivationKit(false);
    }
  }

  function reactivateArchivedIntegration() {
    if (!overview || !selectedArchivedIntegration || !reactivationRecoveryKit || !overview.controller.publicKey ||
        reactivationConfirmation !== 'REACTIVATE') return;
    setReactivationError('');
    if (sourceHasRepository && !integrationSwitchProofConfirmed) {
      setReactivationError('Export, download, and acknowledge a fresh integration-switch recovery kit before continuing.');
      return;
    }
    if (overview.repositoryStatus === 'initializing') {
      setReactivationError('Wait for repository initialization to finish before reactivation.');
      return;
    }
    if (overview.connectionStatus === 'attention_required') {
      setReactivationError('Reauthorize or disconnect the current Google Drive integration before reactivation.');
      return;
    }
    if (overview.config.repositoryInitialized && !overview.currentGeneration?.repositoryId && !overview.config.repositoryId) {
      setReactivationError('The current repository identity is incomplete.');
      return;
    }
    const connected = overview.connectionStatus === 'connected' && overview.config.google.connected;
    if (connected && !overview.config.repositoryInitialized) {
      setReactivationError('Choose a different Google account to reset the uninitialized connection before reactivation.');
      return;
    }
    if (connected && overview.config.google.accountId !== reactivationRecoveryKit.kitIdentity.googleAccountId) {
      setReactivationError('Disconnect the current integration before authorizing this archived Google account.');
      return;
    }
    const targetGenerationId = selectedArchivedIntegration.id;
    const payload: BackupReactivateRepositoryGenerationPayload = {
      targetGenerationId,
      kitIdentity: reactivationRecoveryKit.kitIdentity,
      encryptedPassword: reactivationRecoveryKit.encryptedPassword,
      confirmation: 'REACTIVATE',
    };
    setSelectedArchivedIntegration(null);
    setReactivationConfirmation('');
    requestOperation({
      type: 'reactivate_repository_generation',
      payload,
      label: 'Archived integration reactivation',
      authorization: !connected,
      reactivation: { targetGenerationId, kit: reactivationRecoveryKit },
    }, true);
  }

  async function acknowledgeRecoveryKit() {
    if (!availableRecoveryKit || downloadedRecoveryOperationId !== availableRecoveryKit.operationId) return;
    const recovery = availableRecoveryKit;
    setBusyAction('acknowledge_recovery_kit');
    setActionError('');
    try {
      await api.acknowledgeRecoveryKit(recovery.operationId);
      const next = await loadOverview('poll');
      if (recovery.purpose === 'fresh_start') {
        if (!next?.freshStartProofAcknowledged || !next.lifecycleActions.detach_repository_generation.available) {
          throw new Error('Fresh-start proof was not confirmed by the server.');
        }
        setFreshStartAcknowledgedOperationId(recovery.operationId);
        notify('Fresh-start recovery kit download acknowledged for this operation.', 'success');
      } else if (recovery.purpose === 'integration_switch') {
        if (!next?.integrationSwitchProofAcknowledged) {
          throw new Error('Integration-switch proof was not confirmed by the server.');
        }
        setIntegrationSwitchAcknowledgedOperationId(recovery.operationId);
        setAvailableRecoveryKit(null);
        setDownloadedRecoveryOperationId('');
        notify('Switch-away recovery kit download acknowledged for this operation.', 'success');
      } else {
        setAvailableRecoveryKit(null);
        setDownloadedRecoveryOperationId('');
        notify('Recovery kit storage acknowledged.', 'success');
      }
    } catch (reason) {
      setActionError(reason instanceof ApiError
        ? errorMessage(reason, 'Unable to acknowledge the recovery-kit download.')
        : `The server did not confirm ${recovery.purpose === 'integration_switch' ? 'integration-switch' : 'fresh-start'} recovery proof. Export and download a new kit before continuing.`);
    } finally {
      setBusyAction('');
    }
  }

  function saveSchedule(event: FormEvent) {
    event.preventDefault();
    const timezone = schedule.timezone.trim();
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.time) || !timezoneIsValid(timezone)) {
      setActionError('Choose a valid local time and IANA timezone.');
      return;
    }
    requestOperation({
      type: 'update_schedule',
      payload: { frequency: 'daily', time: schedule.time, timezone },
      label: 'Schedule update',
    }, true);
  }

  function saveRetention(event: FormEvent) {
    event.preventDefault();
    const valid = (Object.keys(RETENTION_BOUNDS) as Array<keyof typeof RETENTION_BOUNDS>).every((key) => {
      const value = retention[key];
      const bounds = RETENTION_BOUNDS[key];
      return Number.isInteger(value) && value >= bounds.min && value <= bounds.max;
    });
    if (!valid) {
      setActionError('Retention values must stay within the displayed bounds.');
      return;
    }
    requestOperation({
      type: 'update_retention',
      payload: retention,
      label: 'Retention update',
    }, true);
  }

  async function cancelOperation() {
    if (!activeOperation?.cancellable) return;
    const reactivation = activeOperation.type === 'reactivate_repository_generation';
    const confirmation = reactivation
      ? 'Request cancellation and cleanup? If the generation switch has not committed, the current integration stays current while OAuth state and candidate credentials are removed. A committed switch cannot be reversed by cancellation.'
      : 'Cancel this operation before the controller commits its work?';
    if (!window.confirm(confirmation)) return;
    setBusyAction('cancel');
    setActionError('');
    try {
      await api.cancelBackupOperation(activeOperation.id);
      notify(reactivation
        ? 'Cancellation requested. Reactivation stays active until candidate and OAuth cleanup is confirmed.'
        : 'Cancellation requested. The controller will confirm when cleanup is complete.');
      await loadOverview('poll');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to request cancellation.'));
    } finally {
      setBusyAction('');
    }
  }

  function openGoogleAuthorization() {
    if (!googleAuthorizationUrl) return;
    const authorizationWindow = window.open(googleAuthorizationUrl, 'streamline-google-drive');
    if (authorizationWindow) {
      authorizationWindow.opener = null;
      authorizationWindowRef.current = authorizationWindow;
    }
  }

  function closePendingOperation() {
    if (reauthenticating) return;
    if (pendingOperation?.recoveryPurpose === 'integration_switch') {
      setSelectedArchivedIntegration(reactivationDialogTargetRef.current);
      reactivationDialogTargetRef.current = null;
    } else if (pendingOperation?.reactivation) {
      const target = overview?.archivedIntegrations.find((integration) => integration.id === pendingOperation.reactivation?.targetGenerationId) ?? null;
      setSelectedArchivedIntegration(target);
      setReactivationRecoveryKit(target ? pendingOperation.reactivation.kit : null);
      setReactivationConfirmation(target ? 'REACTIVATE' : '');
    }
    setPendingOperation(null);
    setPassword('');
    setReauthError('');
  }

  function closeReactivationDialog() {
    recoveryImportEpochRef.current += 1;
    setProcessingReactivationKit(false);
    reactivationDialogTargetRef.current = null;
    setSelectedArchivedIntegration(null);
    setReactivationRecoveryKit(null);
    setReactivationConfirmation('');
    if (availableRecoveryKit?.purpose === 'integration_switch') {
      setAvailableRecoveryKit(null);
      setDownloadedRecoveryOperationId('');
    }
    if (integrationSwitchExportOperationId && integrationSwitchAcknowledgedOperationId !== integrationSwitchExportOperationId) {
      recoveryPrivateKeysRef.current.delete(integrationSwitchExportOperationId);
      consumedRecoveryResultsRef.current.delete(integrationSwitchExportOperationId);
      setIntegrationSwitchExportOperationId('');
      setIntegrationSwitchAcknowledgedOperationId('');
    }
  }

  function openReactivationDialog(integration: BackupArchivedIntegration) {
    recoveryImportEpochRef.current += 1;
    setProcessingReactivationKit(false);
    setActionError('');
    setReactivationRecoveryKit(null);
    setReactivationConfirmation('');
    setSelectedArchivedIntegration(integration);
  }

  function openManageConnection() {
    closeReactivationDialog();
    setDisconnectConfirmation('');
    setFreshStartConfirmation('');
    setLifecycleDialog('manage');
  }

  function openLifecycleConfirmation(dialog: Exclude<LifecycleDialog, 'manage' | null>) {
    setLifecycleDialog(dialog);
    if (dialog === 'disconnect') setDisconnectConfirmation('');
    if (dialog === 'fresh_start') {
      setFreshStartConfirmation('');
      setFreshStartAcknowledgedOperationId('');
      setFreshStartExportOperationId('');
      if (availableRecoveryKit?.purpose === 'fresh_start') {
        setAvailableRecoveryKit(null);
        setDownloadedRecoveryOperationId('');
      }
    }
  }

  if (loading && !overview) return <section className="backup-panel app-page"><div className="panel-loading"><span className="spinner spinner--large" />Loading Backup & Recovery…</div></section>;

  const controllerStatus = overview?.controller.status ?? 'unavailable';
  const overallState = overview?.overallState ?? 'unavailable';
  const timezoneOptions = supportedTimezones();
  const latestRestoreTest = overview?.restoreTests.reduce<BackupRestoreTest | undefined>((latest, test) => (
    !latest || restoreTestTimestamp(test) > restoreTestTimestamp(latest) ? test : latest
  ), undefined);
  const latestPreflight = findLatestOperationOfType(overview?.history ?? [], 'preflight');
  const latestSuccessfulRestoreTest = overview?.restoreTests
    .filter((test) => ['passed', 'succeeded', 'success'].includes(test.state))
    .reduce<BackupRestoreTest | undefined>((latest, test) => (
      !latest || restoreTestTimestamp(test) > restoreTestTimestamp(latest) ? test : latest
    ), undefined);

  const repositoryStatus = overview?.lastRepositoryCheck?.status ?? 'not checked';
  const restoreStatus = latestRestoreTest?.state ?? 'not run';
  const preflightStatus = latestPreflight?.state ?? 'not run';
  const reactivationConnected = overview?.connectionStatus === 'connected' && Boolean(overview.config.google.connected);
  const reactivationNeedsDisconnect = Boolean(
    reactivationConnected && reactivationRecoveryKit &&
    overview?.config.google.accountId !== reactivationRecoveryKit.kitIdentity.googleAccountId,
  );
  const reactivationNeedsAccountReset = Boolean(reactivationConnected && !overview?.config.repositoryInitialized);
  const latestCatchUpState = overview?.currentActivation?.latestCatchUpOperation?.state;
  const catchUpButtonLabel = ['failed', 'cancelled'].includes(latestCatchUpState ?? '') ? 'Retry backup' : 'Back up now';

  return <section className="backup-panel app-page" aria-labelledby="backup-recovery-title">
    <header className="content-header backup-panel__header">
      <div><span className="eyebrow">Owner controls</span><h1 id="backup-recovery-title">Backup & Recovery</h1><p>Protect this installation with encrypted, offsite Google Drive backups.</p></div>
      <button className="button button--secondary" type="button" onClick={() => void loadOverview('manual')} disabled={loading || refreshing}><Icon name="refresh" size={17} />{refreshing ? 'Refreshing…' : 'Refresh'}</button>
    </header>

    {loadError && <p className="form-alert" role="alert">{loadError}</p>}
    <BackupReactivationDialog
      integration={selectedArchivedIntegration}
      overview={overview}
      sourceHasRepository={sourceHasRepository}
      switchProofConfirmed={integrationSwitchProofConfirmed}
      switchExportOperationId={integrationSwitchExportOperationId}
      switchKitReady={Boolean(availableRecoveryKit?.purpose === 'integration_switch' && availableRecoveryKit.operationId === integrationSwitchExportOperationId)}
      switchKitDownloaded={Boolean(availableRecoveryKit && downloadedRecoveryOperationId === availableRecoveryKit.operationId)}
      targetKit={reactivationRecoveryKit}
      confirmation={reactivationConfirmation}
      error={reactivationError}
      processingRecoveryResult={processingRecoveryResult || processingReactivationKit}
      busyAction={busyAction}
      actionDisabled={actionDisabled || !sourceReactivationEligible}
      needsDisconnect={reactivationNeedsDisconnect}
      needsAccountReset={reactivationNeedsAccountReset}
      onClose={closeReactivationDialog}
      onExportSwitchKit={() => void exportRecoveryKit('integration_switch')}
      onDownloadSwitchKit={() => { if (availableRecoveryKit?.purpose === 'integration_switch') { downloadRecoveryKit(availableRecoveryKit.kit); setDownloadedRecoveryOperationId(availableRecoveryKit.operationId); } }}
      onAcknowledgeSwitchKit={() => void acknowledgeRecoveryKit()}
      onLoadTargetKit={(file) => void importReactivationRecoveryKit(file)}
      onRemoveTargetKit={() => { recoveryImportEpochRef.current += 1; setProcessingReactivationKit(false); setReactivationRecoveryKit(null); setReactivationConfirmation(''); }}
      onConfirmationChange={setReactivationConfirmation}
      onReactivate={reactivateArchivedIntegration}
      onManageConnection={openManageConnection}
    />
    {actionError && <p className="form-alert" role="alert">{actionError}</p>}

    {!setupComplete && <article className={`backup-controller-card backup-controller-card--${statusTone(controllerStatus)}`} aria-label="Backup controller readiness">
      <span className="backup-controller-card__icon"><Icon name="server" size={24} /></span>
      <div><span>Backup controller</span><h2>{overview?.controller.ready ? 'Controller ready' : overview?.controller.status === 'configuration_required' ? 'Controller setup required' : 'Controller unavailable'}</h2><p>{overview?.controller.message ?? (overview?.controller.ready ? 'Durable operations continue on the server after you leave this page.' : 'Setup and backup actions remain disabled until the controller reports ready.')}</p><small>Heartbeat {formatTimestamp(overview?.controller.heartbeatAt)}{overview?.controller.version ? ` · Version ${overview.controller.version}` : ''}{overview?.controller.capabilities?.resticVersion ? ` · ${overview.controller.capabilities.resticVersion}` : ''}</small></div>
      <span className={`status-badge status-badge--${statusTone(overview?.controller.compatible === false ? 'incompatible' : controllerStatus)}`}><span className="status-badge__dot" />{overview?.controller.compatible === false ? 'incompatible' : controllerStatus}</span>
    </article>}

    {setupComplete && <article className={`backup-overview-card backup-overview-card--${statusTone(overallState)}`} aria-label="Backup protection summary">
      <div className="backup-overview-card__hero">
        <span className="backup-overview-card__icon"><Icon name="shield" size={24} /></span>
        <div><span>Protection state</span><h2>{overallState.replace(/_/g, ' ')}</h2><p>{overview?.connectionStatus === 'disconnected' ? 'Google Drive is disconnected. Repository records remain available read-only until the exact pinned destination is reauthorized.' : `${overview?.controller.ready ? 'Controller ready' : `Controller ${controllerStatus.replace(/_/g, ' ')}`} · Durable operations continue after you leave this page.`}</p></div>
        <span className={`status-badge status-badge--${statusTone(overallState)}`}><span className="status-badge__dot" />{overallState.replace(/_/g, ' ')}</span>
      </div>
      <dl className="backup-overview-metrics">
        <div><dt>Last verified</dt><dd>{formatTimestamp(overview?.lastVerifiedAt)}</dd></div>
        <div><dt>Next backup</dt><dd>{overview?.connectionStatus === 'disconnected' || !overview?.config.schedule.enabled ? 'Paused' : formatTimestamp(overview?.config.schedule.nextRunAt)}</dd></div>
        <div><dt>Repository integrity</dt><dd className={`backup-overview-status backup-overview-status--${statusTone(repositoryStatus)}`}><span />{repositoryStatus}</dd></div>
        <div><dt>Restore assurance</dt><dd className={`backup-overview-status backup-overview-status--${statusTone(restoreStatus)}`}><span />{restoreStatus}</dd></div>
      </dl>
    </article>}

    {setupComplete && (overview?.scheduleOverlapWarning?.active || overview?.controller.resourceGovernance) && <BackupResourceBanner overview={overview} />}

    {setupComplete && overview?.currentActivation?.kind === 'reactivated' && overview.currentActivation.freshness === 'catch_up_required' && <article className="backup-activation-callout backup-activation-callout--warning" role="status">
      <Icon name="warning" size={20} />
      <div><div className="backup-activation-callout__heading"><div><strong>Integration reactivated successfully</strong><span className="status-badge status-badge--processing"><span className="status-badge__dot" />catch-up required</span></div></div><p>{overview.currentActivation.lastVerifiedBackupAt
        ? <>Its latest known verified backup is from <strong>{formatTimestamp(overview.currentActivation.lastVerifiedBackupAt)}</strong>.</>
        : overview.currentActivation.lastSuccessfulBackupAt
          ? <>Its latest known successful backup is from <strong>{formatTimestamp(overview.currentActivation.lastSuccessfulBackupAt)}</strong>.</>
          : 'No backup is known for this destination; an initial backup is required.'}</p><p>Back up now to include the latest database and media in this destination. This captures files currently on the VPS; it cannot reconstruct a file that existed only while this integration was detached and has since been deleted.</p><button className="button button--primary" type="button" disabled={backupNowDisabled} title={backupNowDisabled ? overview.backupNowBlockedReason : undefined} onClick={() => requestOperation({ type: 'backup_now', label: 'Catch-up backup' })}>{busyAction === 'backup_now' ? 'Queueing…' : catchUpButtonLabel}</button>{backupNowDisabled && overview.backupNowBlockedReason && <small>{overview.backupNowBlockedReason}</small>}</div>
    </article>}
    {setupComplete && overview?.currentActivation?.kind === 'reactivated' && overview.currentActivation.freshness === 'fresh' && <article className="backup-activation-callout backup-activation-callout--success" role="status"><Icon name="check" size={20} /><div><div className="backup-activation-callout__heading"><div><strong>Reactivated integration is fresh</strong><span className="status-badge status-badge--ready"><span className="status-badge__dot" />fresh</span></div></div><p>A post-reactivation snapshot was confirmed {formatTimestamp(overview.currentActivation.freshnessConfirmedAt)}.</p></div></article>}

    {displayedOperation && <article className={`backup-active-operation${latestFailedOperation ? ' backup-active-operation--failed' : ''}`} aria-live={activeOperation ? 'polite' : undefined}>
      <div className="backup-active-operation__heading"><div><span>{activeOperation ? 'Active operation' : 'Latest failed operation'}</span><h2>{operationLabel(displayedOperation.type)}</h2></div><span className={`status-badge status-badge--${statusTone(displayedOperation.state)}`}><span className="status-badge__dot" />{displayedOperation.state}</span></div>
      <p>{displayedOperation.message ?? (activeOperation ? 'The controller is working. This status is safe to close and revisit.' : 'The backup operation did not complete.')}</p>
      {displayedOperation.phase && <small>{activeOperation ? 'Current' : 'Failed'} phase: {displayedOperation.phase.replace(/_/g, ' ')}</small>}
      {displayedOperation.transitionSubstate && <small>Transition: {displayedOperation.transitionSubstate.replace(/_/g, ' ')}</small>}
      {activeOperation?.type === 'reactivate_repository_generation' && activeOperation.transitionSubstate === 'cancellation_requested' && <p className="backup-active-operation__cleanup" role="status">Cancellation has been requested. This operation remains active until OAuth state and candidate credentials are cleaned up. If the generation switch already committed, the reactivated target remains current.</p>}
      {displayedOperationTimestamp && <time className="backup-active-operation__timestamp" dateTime={displayedOperationTimestamp}>{activeOperation ? 'Started' : 'Finished'} {formatTimestamp(displayedOperationTimestamp)}</time>}
      {displayedOperation.progress !== null && <div className="backup-operation-progress" role="progressbar" aria-label={`${operationLabel(displayedOperation.type)} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={displayedOperation.progress}><span style={{ width: `${displayedOperation.progress}%` }} /></div>}
      {activeOperation && <div className="backup-active-operation__actions">{googleAuthorizationUrl && OAUTH_OPERATION_TYPES.has(activeOperation.type as BackupOperationType) && <button className="button button--primary" type="button" onClick={openGoogleAuthorization}>Open Google authorization</button>}{activeOperation.cancellable && <button className="button button--secondary" type="button" disabled={busyAction === 'cancel'} onClick={() => void cancelOperation()}>{busyAction === 'cancel' ? 'Requesting…' : activeOperation.type === 'reactivate_repository_generation' ? 'Request cancellation & cleanup' : 'Cancel queued operation'}</button>}</div>}
    </article>}

    {!setupComplete ? <div className="backup-setup">
      <ol className="backup-steps" aria-label="Backup setup progress">
        {['Controller', 'Google Drive', 'Encrypt & initialize', 'Schedule & retention', 'Protect now'].map((label, index) => {
          const step = index + 1;
          return <li key={label} className={step < setupStage ? 'is-complete' : step === setupStage ? 'is-current' : ''} aria-current={step === setupStage ? 'step' : undefined}><span>{step < setupStage ? <Icon name="check" size={14} /> : step}</span>{label}</li>;
        })}
      </ol>

      {Boolean(overview?.archivedIntegrations.length) && <ArchivedIntegrationsList integrations={overview?.archivedIntegrations ?? []} disabled={actionDisabled || !sourceReactivationEligible || !overview?.lifecycleActions.reactivate_repository_generation.available} onReactivate={openReactivationDialog} />}

      {setupStage === 1 && <article className="backup-setup-card">
        <span className="backup-setup-card__icon"><Icon name="server" size={25} /></span><div><span className="eyebrow">Step 1 of 5</span><h2>Wait for the backup controller</h2><p>The controller must be online and version-compatible before credentials or repository settings can be changed.</p><button className="button button--secondary" type="button" onClick={() => void loadOverview('manual')} disabled={refreshing}><Icon name="refresh" size={16} />Check readiness</button></div>
      </article>}

      {setupStage === 2 && <article className="backup-setup-card backup-setup-card--form">
        <span className="backup-setup-card__icon"><Icon name="hard-drive" size={25} /></span><div><span className="eyebrow">Step 2 of 5</span><h2>Connect Google Drive</h2><p>Authorize the Google account that will own the encrypted backup objects. Google Drive is offsite storage, but it is not immutable storage.</p>
          <div className="backup-destination-note"><strong>Dedicated destination</strong><span>{branding.productName} pins the selected folder identity so a reconnect cannot silently create a second backup history.</span></div>
          <label className="backup-field">Drive folder name<input value={destinationFolderName} onChange={(event) => { setDestinationFolderName(event.target.value); if (importedRecoveryKit && event.target.value !== importedRecoveryKit.googleDriveFolderName) setImportedRecoveryKit(null); }} maxLength={180} autoComplete="off" aria-describedby="backup-folder-hint" required readOnly={Boolean(importedRecoveryKit)} /></label>
          <small id="backup-folder-hint">Use a dedicated folder such as Streamline-Backups/Streamline. A raw repository location is never accepted here.</small>
          <div className="backup-recovery-import"><strong>Recover an existing installation?</strong><span>Load its recovery kit before OAuth so {branding.productName} can require the original Google account and stable folder ID.</span><label className="button button--secondary">Choose recovery-kit JSON<input className="sr-only" type="file" accept="application/json,.json" onChange={(event) => { void importRecoveryKit(event.target.files?.[0]); event.target.value = ''; }} /></label>{importedRecoveryKit && <small>Loaded repository <code>{importedRecoveryKit.repositoryId.slice(0, 8)}</code> from {importedRecoveryKit.googleAccountEmail}. <button className="text-button" type="button" onClick={() => { setImportedRecoveryKit(null); setDestinationFolderName('Streamline-Backups/Streamline'); }}>Remove</button></small>}</div>
          <div className="backup-setup-card__actions"><button className="button button--primary" type="button" onClick={connectGoogle} disabled={actionDisabled || !destinationFolderName.trim()}>{busyAction === 'connect_google' ? 'Connecting…' : importedRecoveryKit ? 'Confirm password & reconnect Google' : 'Confirm password & connect Google'}</button>{overview?.lifecycleActions.reset_google_before_initialization.available && <button className="button button--secondary" type="button" disabled={actionDisabled} onClick={() => openLifecycleConfirmation('reset')}>Choose a different Google account</button>}{googleAuthorizationUrl && <button className="button button--secondary" type="button" onClick={openGoogleAuthorization}>Open Google authorization</button>}</div>
        </div>
      </article>}

      {setupStage === 3 && <article className="backup-setup-card backup-setup-card--form">
        <span className="backup-setup-card__icon"><Icon name="shield" size={25} /></span><div><span className="eyebrow">Step 3 of 5</span><h2>{overview?.config.repositoryInitialized ? 'Save the recovery kit' : importedRecoveryKit ? 'Reconnect encrypted repository' : 'Encrypt and initialize'}</h2><p>{overview?.config.repositoryInitialized ? 'The repository is ready, but protection setup remains incomplete until you download and acknowledge its recovery kit.' : importedRecoveryKit ? 'The browser encrypts the imported repository password directly to this controller. Plaintext never enters the app API or database.' : 'The controller generates a high-entropy repository password and returns it only inside a browser-encrypted recovery kit.'}</p>
          <dl className="backup-destination-summary"><div><dt>Google account</dt><dd>{overview?.config.google.account ?? 'Connected account'}</dd></div><div><dt>Drive folder</dt><dd>{overview?.config.google.folderName ?? destinationFolderName}</dd></div>{overview?.config.google.folderId && <div><dt>Stable folder ID</dt><dd>{overview.config.google.folderId}</dd></div>}{overview?.config.repositoryId && <div><dt>Repository ID</dt><dd><code>{overview.config.repositoryId.slice(0, 12)}</code></dd></div>}<div><dt>Estimated local data</dt><dd>{formatBytes(overview?.config.estimatedLocalBytes)}</dd></div></dl>
          <div className="backup-recovery-guidance"><Icon name="warning" size={18} /><div><strong>Store the recovery kit outside this server.</strong><span>Keep it in a password manager and a second protected location. Losing the recovery password makes the backup unrecoverable.</span></div></div>
          {availableRecoveryKit ? <div className="backup-recovery-download"><strong>Recovery kit ready in this browser</strong><span>Download it now. {branding.productName} does not retain the plaintext kit in the application or database.</span><div className="backup-setup-card__actions"><button className="button button--primary" type="button" onClick={() => { downloadRecoveryKit(availableRecoveryKit.kit); setDownloadedRecoveryOperationId(availableRecoveryKit.operationId); }}>Download recovery kit</button><button className="button button--secondary" type="button" disabled={downloadedRecoveryOperationId !== availableRecoveryKit.operationId || busyAction === 'acknowledge_recovery_kit'} onClick={() => void acknowledgeRecoveryKit()}>{busyAction === 'acknowledge_recovery_kit' ? 'Saving…' : 'I stored it safely'}</button></div><small>The acknowledgement button unlocks only after this browser starts the download.</small></div> : overview?.config.repositoryInitialized ? <button className="button button--primary" type="button" onClick={() => void exportRecoveryKit()} disabled={actionDisabled || processingRecoveryResult || !overview.controller.publicKey}>{busyAction === 'export_recovery_kit' || busyAction === 'preparing_recovery_key' || processingRecoveryResult ? 'Preparing…' : 'Confirm password & export recovery kit'}</button> : importedRecoveryKit ? <><p>Recovery kit repository: <code>{importedRecoveryKit.repositoryId.slice(0, 12)}</code></p><button className="button button--primary" type="button" onClick={() => void reconnectRepository()} disabled={actionDisabled || !overview?.controller.publicKey}>{busyAction === 'reconnect_repository' || busyAction === 'preparing_recovery_key' ? 'Preparing…' : 'Confirm password & reconnect repository'}</button><button className="button button--secondary" type="button" onClick={() => setImportedRecoveryKit(null)} disabled={Boolean(activeOperation)}>Use a new repository instead</button></> : <><label className="backup-field">Type INITIALIZE to create a new repository<input value={initializationConfirmation} onChange={(event) => setInitializationConfirmation(event.target.value)} autoComplete="off" spellCheck={false} /></label><button className="button button--primary" type="button" onClick={() => void initializeRepository()} disabled={actionDisabled || initializationConfirmation !== 'INITIALIZE' || !overview?.controller.publicKey}>{busyAction === 'initialize_repository' || busyAction === 'preparing_recovery_key' ? 'Preparing…' : 'Confirm password & initialize repository'}</button><label className="button button--secondary">Reconnect with recovery kit<input className="sr-only" type="file" accept="application/json,.json" onChange={(event) => { void importRecoveryKit(event.target.files?.[0]); event.target.value = ''; }} /></label></>}
          {!overview?.config.repositoryInitialized && overview?.lifecycleActions.reset_google_before_initialization.available && <button className="button button--secondary" type="button" disabled={actionDisabled} onClick={() => openLifecycleConfirmation('reset')}>Choose a different Google account</button>}
          <small>Initialization is explicit and never happens during a normal backup. Reconnect only opens and verifies the repository ID from the selected recovery kit.</small>
        </div>
      </article>}

      {setupStage === 4 && <>
        <div className="backup-policy-grid">
          <ScheduleForm schedule={schedule} setSchedule={setSchedule} timezoneOptions={timezoneOptions} busy={busyAction === 'update_schedule'} disabled={actionDisabled} onSubmit={saveSchedule} />
          <RetentionForm retention={retention} setRetention={setRetention} busy={busyAction === 'update_retention'} disabled={actionDisabled} onSubmit={saveRetention} />
        </div>
        <article className="backup-setup-card">
          <span className="backup-setup-card__icon"><Icon name="upload" size={25} /></span>
          <div><span className="eyebrow">Manual validation</span><h2>Back up while the schedule stays paused</h2><p>Run one deliberate backup after validating a deployment without enabling automatic daily runs. You can save the schedule after the backup is verified.</p><button className="button button--primary" type="button" disabled={backupNowDisabled} title={backupNowDisabled ? overview?.backupNowBlockedReason : undefined} onClick={() => requestOperation({ type: 'backup_now', label: 'Backup' })}>{busyAction === 'backup_now' ? 'Queueing…' : 'Back up now (schedule stays paused)'}</button></div>
        </article>
      </>}
    </div> : <>
      {!overview?.lastVerifiedAt && repositoryConnectedReady && <div className="backup-get-started"><Icon name="shield" size={21} /><div><strong>Validate and protect this installation now</strong><span>Run preflight, start the initial backup, check the repository, discover snapshots, then run an isolated restore test. Long uploads continue without an open browser.</span></div></div>}
      {routineBlockedReason && <div className="backup-recovery-guidance" role="status"><Icon name="warning" size={18} /><div><strong>Repository actions are read-only</strong><span>{routineBlockedReason} Existing snapshots, restore-test records, and sanitized activity remain visible. The remote repository has not been deleted.</span></div></div>}

      <article className="backup-workspace">
        <div ref={dashboardTabRail} className="backup-workspace__nav" role="tablist" aria-label="Backup and recovery sections">
          {BACKUP_DASHBOARD_VIEWS.map((view) => <button id={`backup-view-tab-${view.id}`} key={view.id} type="button" role="tab" aria-selected={selectedDashboardView === view.id} aria-controls={`backup-view-panel-${view.id}`} tabIndex={selectedDashboardView === view.id ? 0 : -1} onKeyDown={handleTabKeyDown} onClick={() => selectDashboardView(view.id)}><Icon name={view.icon} size={18} /><span><strong>{view.label}</strong><small>{view.description}</small></span><Icon name="chevron" size={14} /></button>)}
        </div>

        <div className="backup-workspace__content">
          {selectedDashboardView === 'backup' && <section id="backup-view-panel-backup" className="backup-context-panel" role="tabpanel" aria-labelledby="backup-view-tab-backup">
            <div className="backup-context-panel__heading"><div><span className="eyebrow">Routine protection</span><h2>Backups</h2><p>Start a durable backup or review the snapshots already available for recovery.</p></div><span className={`status-badge status-badge--${statusTone(overallState)}`}><span className="status-badge__dot" />{overallState.replace(/_/g, ' ')}</span></div>
            <div className="backup-context-actions backup-context-actions--primary" aria-label="Backup actions">
              <ActionButton icon="upload" label="Back up now" description={backupNowDisabled && overview?.backupNowBlockedReason ? overview.backupNowBlockedReason : 'Queue an encrypted database and media backup.'} disabled={backupNowDisabled} busy={busyAction === 'backup_now'} onClick={() => requestOperation({ type: 'backup_now', label: 'Backup' })} primary />
              <ActionButton icon="archive" label="View snapshots" description={`${overview?.snapshots.length ?? 0} durable snapshots available.`} disabled={routineActionsDisabled} busy={busyAction === 'list_snapshots'} onClick={() => { selectRecordView('snapshots'); requestOperation({ type: 'list_snapshots', label: 'Snapshot discovery' }); }} />
            </div>
            <article className="backup-dashboard-card backup-dashboard-card--compact"><div className="backup-dashboard-card__heading"><div><h2>Current backup status</h2><p>Controller-backed state for the routine protection workflow.</p></div></div><dl><div><dt>Last verified</dt><dd>{formatTimestamp(overview?.lastVerifiedAt)}</dd></div><div><dt>Next scheduled run</dt><dd>{formatTimestamp(overview?.config.schedule.nextRunAt)}</dd></div><div><dt>Available snapshots</dt><dd>{overview?.snapshots.length ?? 0}</dd></div></dl></article>
          </section>}

          {selectedDashboardView === 'health' && <section id="backup-view-panel-health" className="backup-context-panel" role="tabpanel" aria-labelledby="backup-view-tab-health">
            <div className="backup-context-panel__heading"><div><span className="eyebrow">Recovery readiness</span><h2>Health checks</h2><p>Run validation beside the latest safe result from each check.</p></div></div>
            <div className="backup-context-actions backup-context-actions--health" aria-label="Health check actions">
              <ActionButton icon="check" label="Run preflight" description="Validate controller, database, storage, and credentials." disabled={routineActionsDisabled} busy={busyAction === 'preflight'} onClick={() => requestOperation({ type: 'preflight', label: 'Preflight' })} />
              <ActionButton icon="shield" label="Check repository" description="Verify encrypted repository metadata and integrity." disabled={routineActionsDisabled} busy={busyAction === 'repository_check'} onClick={() => requestOperation({ type: 'repository_check', label: 'Repository check' })} />
              <ActionButton icon="refresh" label="Run restore test" description="Restore and verify a known sample in isolated scratch storage." disabled={routineActionsDisabled} busy={busyAction === 'test_restore'} onClick={() => requestOperation({ type: 'test_restore', label: 'Restore test' })} />
            </div>
            <div className="backup-health-results">
              <article className="backup-dashboard-card backup-dashboard-card--compact"><div className="backup-dashboard-card__heading"><div><h2>Preflight</h2><p>Controller, database, storage, and credential checks.</p></div><span className={`status-badge status-badge--${statusTone(preflightStatus)}`}><span className="status-badge__dot" />{preflightStatus}</span></div><dl><div><dt>Last run</dt><dd>{formatTimestamp(latestPreflight?.finishedAt ?? latestPreflight?.startedAt ?? latestPreflight?.createdAt)}</dd></div></dl><p className="backup-dashboard-card__copy">{latestPreflight?.message ?? 'Run preflight to establish the current readiness state.'}</p></article>
              <article className="backup-dashboard-card backup-dashboard-card--compact"><div className="backup-dashboard-card__heading"><div><h2>Repository integrity</h2><p>Encrypted metadata and repository consistency.</p></div><span className={`status-badge status-badge--${statusTone(repositoryStatus)}`}><span className="status-badge__dot" />{repositoryStatus}</span></div><dl><div><dt>Last check</dt><dd>{formatTimestamp(overview?.lastRepositoryCheck?.finishedAt)}</dd></div></dl><p className="backup-dashboard-card__copy">{overview?.lastRepositoryCheck?.message ?? 'Check the repository to establish its current integrity status.'}</p></article>
              <article className="backup-dashboard-card backup-dashboard-card--compact"><div className="backup-dashboard-card__heading"><div><h2>Restore assurance</h2><p>Isolated restoration of a known media sample.</p></div><span className={`status-badge status-badge--${statusTone(restoreStatus)}`}><span className="status-badge__dot" />{restoreStatus}</span></div><dl><div><dt>Last test</dt><dd>{formatTimestamp(latestRestoreTest?.finishedAt ?? latestRestoreTest?.createdAt)}</dd></div></dl><p className="backup-dashboard-card__copy">{latestRestoreTest?.message ?? (latestRestoreTest ? `${latestRestoreTest.sampleName ?? 'Known media sample'} completed with status ${latestRestoreTest.state}.` : 'Run an isolated restore test to establish recovery assurance.')}</p></article>
            </div>
          </section>}

          {selectedDashboardView === 'automation' && <section id="backup-view-panel-automation" className="backup-context-panel" role="tabpanel" aria-labelledby="backup-view-tab-automation">
            <div className="backup-context-panel__heading"><div><span className="eyebrow">Controller automation</span><h2>Schedule & retention</h2><p>Review and change automatic backup timing and restore-point limits in one place.</p></div></div>
            <div className="backup-context-note"><Icon name="refresh" size={18} /><div><strong>{routineActionsDisabled || activationReviewPending || !overview?.config.schedule.enabled ? 'Automation paused' : `Next run ${formatTimestamp(overview?.config.schedule.nextRunAt)}`}</strong><span>{routineActionsDisabled ? routineBlockedReason : activationReviewPending ? 'Scheduling remains paused until both the schedule and retention policy are reviewed for this activation. Manual Back up now remains independent.' : !overview?.config.schedule.enabled ? 'The daily schedule is paused. Save the schedule when you are ready to resume automatic backups.' : `Daily at ${overview?.config.schedule.time} in ${overview?.config.schedule.timezone}. The controller runs without an open browser.`}</span></div></div>
            {overview?.currentActivation && <dl className="backup-review-markers" aria-label="Activation review status"><div><dt>Schedule review</dt><dd><span className={`status-badge status-badge--${overview.currentActivation.scheduleReviewed ? 'ready' : 'processing'}`}><span className="status-badge__dot" />{overview.currentActivation.scheduleReviewed ? 'reviewed' : 'required'}</span></dd></div><div><dt>Retention review</dt><dd><span className={`status-badge status-badge--${overview.currentActivation.retentionReviewed ? 'ready' : 'processing'}`}><span className="status-badge__dot" />{overview.currentActivation.retentionReviewed ? 'reviewed' : 'required'}</span></dd></div></dl>}
            <div className="backup-policy-grid backup-policy-grid--workspace"><ScheduleForm eyebrow="Automation" schedule={schedule} setSchedule={setSchedule} timezoneOptions={timezoneOptions} busy={busyAction === 'update_schedule'} disabled={routineActionsDisabled} onSubmit={saveSchedule} /><RetentionForm eyebrow="Automation" retention={retention} setRetention={setRetention} busy={busyAction === 'update_retention'} disabled={routineActionsDisabled} onSubmit={saveRetention} /></div>
          </section>}

          {selectedDashboardView === 'storage-recovery' && <section id="backup-view-panel-storage-recovery" className="backup-context-panel" role="tabpanel" aria-labelledby="backup-view-tab-storage-recovery">
            <div className="backup-context-panel__heading"><div><span className="eyebrow">Offsite ownership</span><h2>Storage & recovery</h2><p>Manage the pinned Google Drive destination and the recovery material stored outside this server.</p></div></div>
            <div className="backup-dashboard-grid">
              <article className="backup-dashboard-card"><div className="backup-dashboard-card__heading"><div><h2>Google Drive destination</h2><p>Encrypted offsite storage; not immutable.</p></div><span className={`status-badge status-badge--${overview?.config.google.connected ? 'ready' : 'failed'}`}><span className="status-badge__dot" />{overview?.config.google.connected ? 'connected' : 'disconnected'}</span></div><dl><div><dt>Account</dt><dd>{overview?.config.google.account ?? 'Pinned account'}</dd></div><div><dt>Folder</dt><dd>{overview?.config.google.folderName ?? 'Dedicated backup folder'}</dd></div>{overview?.config.google.folderId && <div><dt>Stable folder ID</dt><dd>{overview.config.google.folderId}</dd></div>}{overview?.config.repositoryId && <div><dt>Repository ID</dt><dd><code>{overview.config.repositoryId.slice(0, 12)}</code></dd></div>}</dl><p className="backup-dashboard-card__copy">Reauthorization must return the exact pinned Google account, folder, and initialized repository. A mismatch leaves existing credentials unchanged; {branding.productName} does not revoke access during reauthorization.</p><div className="backup-setup-card__actions"><button className="button button--primary" type="button" disabled={actionDisabled || !overview?.lifecycleActions.reauthorize_google.available} onClick={reauthorizeGoogle}>Reauthorize Google Drive</button><button className="button button--secondary" type="button" disabled={actionDisabled} onClick={openManageConnection}>Manage connection</button></div>{!overview?.lifecycleActions.reauthorize_google.available && overview?.lifecycleActions.reauthorize_google.blockedReason && <small role="status">{overview.lifecycleActions.reauthorize_google.blockedReason}</small>}</article>
              <article className="backup-dashboard-card"><div className="backup-dashboard-card__heading"><div><h2>Recovery kit</h2><p>Recovery material must be stored outside the VPS.</p></div><span className={`status-badge status-badge--${overview?.config.recoveryKitAcknowledged ? 'ready' : 'failed'}`}><span className="status-badge__dot" />{overview?.config.recoveryKitAcknowledged ? 'acknowledged' : 'action required'}</span></div><p className="backup-dashboard-card__copy">The repository password is never displayed by normal APIs. Export decrypts a controller-encrypted copy only inside this browser for local download.</p>{availableRecoveryKit?.purpose === 'manual' ? <div className="backup-recovery-download"><button className="button button--primary" type="button" onClick={() => { downloadRecoveryKit(availableRecoveryKit.kit); setDownloadedRecoveryOperationId(availableRecoveryKit.operationId); }}>Download recovery kit</button><button className="button button--secondary" type="button" disabled={downloadedRecoveryOperationId !== availableRecoveryKit.operationId || busyAction === 'acknowledge_recovery_kit'} onClick={() => void acknowledgeRecoveryKit()}>I stored it safely</button></div> : <button className="button button--secondary" type="button" onClick={() => void exportRecoveryKit()} disabled={actionDisabled || processingRecoveryResult || !overview?.controller.publicKey}>{busyAction === 'export_recovery_kit' || busyAction === 'preparing_recovery_key' || processingRecoveryResult ? 'Preparing…' : 'Export recovery kit'}</button>}<div className="backup-setup-card__actions"><label className={`button button--secondary${actionDisabled ? ' is-disabled' : ''}`}>Load recovery kit<input className="sr-only" type="file" accept="application/json,.json" disabled={actionDisabled} onChange={(event) => { void importRecoveryKit(event.target.files?.[0]); event.target.value = ''; }} /></label>{importedRecoveryKit && <button className="button button--primary" type="button" onClick={() => void reconnectRepository()} disabled={routineActionsDisabled || !overview?.controller.publicKey}>Confirm password & verify imported kit</button>}</div></article>
            </div>
            <ArchivedIntegrationsList integrations={overview?.archivedIntegrations ?? []} disabled={actionDisabled || !sourceReactivationEligible || !overview?.lifecycleActions.reactivate_repository_generation.available} onReactivate={openReactivationDialog} />
          </section>}

          {selectedDashboardView === 'media-archive' && <section id="backup-view-panel-media-archive" className="backup-context-panel" role="tabpanel" aria-labelledby="backup-view-tab-media-archive">
            <MediaArchivePanel
              imageHostingEnabled={imageHostingEnabled}
              notify={notify}
              onOpenVideo={onOpenVideo}
              hostedMemberFiltering={hostedMemberFiltering}
            />
          </section>}


          {selectedDashboardView === 'records' && <section id="backup-view-panel-records" className="backup-context-panel backup-context-panel--records" role="tabpanel" aria-labelledby="backup-view-tab-records">
            <div className="backup-context-panel__heading"><div><span className="eyebrow">Durable records</span><h2>Backup records</h2><p>Review snapshots, isolated restore tests, and sanitized operation history.</p></div></div>
            <article className="backup-records-card">
        <div ref={recordTabRail} className="backup-record-tabs" role="tablist" aria-label="Backup records">
          <button id="backup-tab-snapshots" type="button" role="tab" aria-selected={selectedRecordView === 'snapshots'} aria-controls="backup-panel-snapshots" tabIndex={selectedRecordView === 'snapshots' ? 0 : -1} onKeyDown={handleTabKeyDown} onClick={() => selectRecordView('snapshots')}>Snapshots <span>{overview?.snapshots.length ?? 0}</span></button>
          <button id="backup-tab-restore-tests" type="button" role="tab" aria-selected={selectedRecordView === 'restore-tests'} aria-controls="backup-panel-restore-tests" tabIndex={selectedRecordView === 'restore-tests' ? 0 : -1} onKeyDown={handleTabKeyDown} onClick={() => selectRecordView('restore-tests')}>Restore tests <span>{overview?.restoreTests.length ?? 0}</span></button>
          <button id="backup-tab-activity" type="button" role="tab" aria-selected={selectedRecordView === 'activity'} aria-controls="backup-panel-activity" tabIndex={selectedRecordView === 'activity' ? 0 : -1} onKeyDown={handleTabKeyDown} onClick={() => selectRecordView('activity')}>Activity <span>{overview?.history.length ?? 0}</span></button>
        </div>

        {selectedRecordView === 'snapshots' && <section id="backup-panel-snapshots" className="backup-record-panel" role="tabpanel" aria-labelledby="backup-tab-snapshots">
          <div className="backup-record-panel__heading"><div><h2>Snapshots</h2><p>Safe summaries only—repository-internal paths are never shown.</p></div><button className="button button--secondary" type="button" disabled={routineActionsDisabled} onClick={() => requestOperation({ type: 'list_snapshots', label: 'Snapshot discovery' })}><Icon name="refresh" size={15} />{busyAction === 'list_snapshots' ? 'Refreshing…' : 'Refresh snapshots'}</button></div>
          {overview?.snapshots.length ? <div className="backup-table-wrap"><table className="video-table backup-table"><thead><tr><th>Snapshot</th><th>Created</th><th>Data size</th><th>Duration</th><th>Verification</th><th>Retention</th></tr></thead><tbody>{overview.snapshots.map((snapshot) => <tr key={snapshot.id}><td data-label="Snapshot"><code>{snapshot.id}</code></td><td data-label="Created">{formatTimestamp(snapshot.createdAt)}</td><td data-label="Data size">{formatBytes(snapshot.sizeBytes)}</td><td data-label="Duration">{formatDuration(snapshot.durationSeconds)}</td><td data-label="Verification"><span className={`status-badge status-badge--${statusTone(snapshot.verificationStatus)}`}><span className="status-badge__dot" />{snapshot.verificationStatus}</span></td><td data-label="Retention">{snapshot.retentionClass ?? '—'}</td></tr>)}</tbody></table></div> : <div className="empty-state backup-empty-state"><span className="empty-state__icon"><Icon name="archive" size={24} /></span><strong>No snapshots discovered</strong><span>Refresh snapshots to ask the controller for the latest inventory.</span></div>}
        </section>}

        {selectedRecordView === 'restore-tests' && <section id="backup-panel-restore-tests" className="backup-record-panel" role="tabpanel" aria-labelledby="backup-tab-restore-tests">
          <div className="backup-record-panel__heading"><div><h2>Restore tests</h2><p>Isolated checks never overwrite production data.</p></div><button className="button button--secondary" type="button" disabled={routineActionsDisabled} onClick={() => requestOperation({ type: 'test_restore', label: 'Restore test' })}><Icon name="refresh" size={15} />{busyAction === 'test_restore' ? 'Starting…' : 'Run restore test'}</button></div>
          {overview?.restoreTests.length ? <ul className="backup-history-list">{overview.restoreTests.slice(0, 6).map((test) => <li key={test.id}><span className={`status-badge status-badge--${statusTone(test.state)}`}><span className="status-badge__dot" />{test.state}</span><div><strong>{test.sampleName ?? 'Known media sample'}</strong><small>{formatTimestamp(test.finishedAt ?? test.createdAt)}</small></div></li>)}</ul> : <p className="backup-list-card__empty">No isolated restore test has been recorded.</p>}
        </section>}

        {selectedRecordView === 'activity' && <section id="backup-panel-activity" className="backup-record-panel" role="tabpanel" aria-labelledby="backup-tab-activity">
          <div className="backup-record-panel__heading"><div><h2>Recent operations</h2><p>Durable controller history with sanitized status details.</p></div></div>
          {overview?.history.length ? <BackupOperationHistoryList operations={overview.history.slice(0, 8)} /> : <p className="backup-list-card__empty">No completed operations yet.</p>}
        </section>}
            </article>
          </section>}
        </div>
      </article>
    </>}

    <Modal open={lifecycleDialog === 'manage'} title="Manage Google connection" description={`Choose how ${branding.productName} should handle the pinned Google Drive destination.`} onClose={() => setLifecycleDialog(null)} footer={<button className="button button--secondary" type="button" onClick={() => setLifecycleDialog(null)}>Close</button>}>
      <div className="backup-lifecycle-options">
        <section><h3>Reauthorize current Google Drive</h3><p>Refresh OAuth access for the exact pinned account, folder, and repository. Selecting a different destination fails safely and does not replace or revoke the existing credential.</p><button className="button button--primary" type="button" disabled={actionDisabled || !overview?.lifecycleActions.reauthorize_google.available} onClick={reauthorizeGoogle}>Confirm password & reauthorize</button>{!overview?.lifecycleActions.reauthorize_google.available && <small>{overview?.lifecycleActions.reauthorize_google.blockedReason}</small>}</section>
        {overview?.lifecycleActions.reset_google_before_initialization.available && <section><h3>Choose a different account</h3><p>Available only before repository initialization. This clears the current OAuth and destination pin; the empty folder already created in Google Drive may remain for manual removal.</p><button className="button button--secondary" type="button" onClick={() => openLifecycleConfirmation('reset')}>Review account reset</button></section>}
        {overview?.config.repositoryInitialized && <section><h3>Disconnect Google Drive access</h3><p>Remove local OAuth while preserving the pinned destination, repository identity, repository password, recovery acknowledgement, and current generation history.</p><button className="button button--secondary" type="button" disabled={!overview.lifecycleActions.disconnect_google.available} onClick={() => openLifecycleConfirmation('disconnect')}>Review disconnection</button>{!overview.lifecycleActions.disconnect_google.available && <small>{overview.lifecycleActions.disconnect_google.blockedReason}</small>}</section>}
        {overview?.config.repositoryInitialized && <section className="backup-lifecycle-options__danger"><h3>Start fresh with another account</h3><p>Detach this generation after exporting its current recovery kit. Local OAuth and the repository password are removed; the old remote repository is never deleted.</p><button className="button button--danger" type="button" disabled={!canBeginFreshStart} onClick={() => openLifecycleConfirmation('fresh_start')}>Review start fresh</button>{!canBeginFreshStart && <small>{activeOperation ? 'Wait for the active operation to finish.' : 'A ready initialized repository and pinned destination are required.'}</small>}</section>}
      </div>
    </Modal>

    <Modal open={lifecycleDialog === 'reset'} title="Choose a different Google account" description="This reset is allowed only before repository initialization." width="small" onClose={() => setLifecycleDialog(null)} footer={<><button className="button button--secondary" type="button" onClick={() => setLifecycleDialog('manage')}>Back</button><button className="button button--danger" type="button" disabled={!overview?.lifecycleActions.reset_google_before_initialization.available} onClick={resetGoogleBeforeInitialization}>Confirm password & reset connection</button></>}>
      <div className="backup-lifecycle-summary"><p>The current Google OAuth credential and account/folder pin will be removed. The empty Drive folder may remain in the old account and can be deleted manually.</p><dl><div><dt>Preserved</dt><dd>Application database, media files, and controller-owned wrapping key/cache</dd></div><div><dt>Reset</dt><dd>Google OAuth and the uninitialized destination pin</dd></div><div><dt>Remote data</dt><dd>No repository exists; {branding.productName} does not delete the empty folder</dd></div></dl></div>
    </Modal>

    <Modal open={lifecycleDialog === 'disconnect'} title="Disconnect Google Drive access" description="Backups and repository actions remain unavailable until this exact destination is reauthorized." width="medium" onClose={() => setLifecycleDialog(null)} footer={<><button className="button button--secondary" type="button" onClick={() => setLifecycleDialog('manage')}>Back</button><button className="button button--danger" type="button" disabled={disconnectConfirmation !== 'DISCONNECT' || !overview?.lifecycleActions.disconnect_google.available} onClick={disconnectGoogle}>Confirm password & disconnect</button></>}>
      <div className="backup-lifecycle-summary"><dl><div><dt>Account</dt><dd>{overview?.config.google.account ?? 'Pinned Google account'}</dd></div><div><dt>Folder</dt><dd>{overview?.config.google.folderName ?? 'Pinned Drive folder'}</dd></div><div><dt>Repository ID</dt><dd><code>{overview?.config.repositoryId?.slice(0, 16) ?? 'Unavailable'}</code></dd></div><div><dt>Preserved</dt><dd>Repository identity, repository password, recovery acknowledgement, snapshots, and restore-test history</dd></div><div><dt>Removed</dt><dd>Local Google OAuth access; scheduling is paused</dd></div><div><dt>Remote repository</dt><dd>Never deleted by this operation</dd></div></dl><label className="backup-field">Type DISCONNECT to continue<input value={disconnectConfirmation} onChange={(event) => setDisconnectConfirmation(event.target.value)} autoComplete="off" spellCheck={false} /></label></div>
    </Modal>

    <Modal open={lifecycleDialog === 'fresh_start'} title="Start fresh with another account" description="Detach this backup generation without deleting its Google Drive repository." width="large" onClose={() => setLifecycleDialog(null)} footer={<button className="button button--secondary" type="button" onClick={() => setLifecycleDialog(null)}>Close</button>}>
      <div className="backup-fresh-start">
        <div className="backup-recovery-guidance"><Icon name="warning" size={18} /><div><strong>The old repository will remain in Google Drive.</strong><span>You will need the recovery kit exported in this flow to open it later. The new account will require a new repository, recovery kit, backup, repository check, snapshot discovery, and restore test.</span></div></div>
        <dl className="backup-destination-summary"><div><dt>Current account</dt><dd>{overview?.config.google.account ?? 'Pinned Google account'}</dd></div><div><dt>Current folder</dt><dd>{overview?.config.google.folderName ?? 'Pinned Drive folder'}</dd></div><div><dt>Repository ID</dt><dd><code>{overview?.config.repositoryId ?? 'Unavailable'}</code></dd></div><div><dt>Last verified backup</dt><dd>{formatTimestamp(overview?.lastVerifiedAt)}</dd></div><div><dt>Last successful restore test</dt><dd>{formatTimestamp(latestSuccessfulRestoreTest?.finishedAt ?? latestSuccessfulRestoreTest?.createdAt)}</dd></div></dl>
        <section className="backup-fresh-start__step"><span>1</span><div><h3>Export this generation’s recovery kit</h3><p>The controller encrypts it to a temporary browser key. Plaintext is decrypted only in this browser.</p>{!freshStartExportOperationId && <button className="button button--primary" type="button" disabled={actionDisabled || processingRecoveryResult || !overview?.controller.publicKey} onClick={() => void exportRecoveryKit('fresh_start')}>Confirm password & export fresh-start recovery kit</button>}{freshStartExportOperationId && !availableRecoveryKit && <p role="status">Preparing recovery kit for operation <code>{freshStartExportOperationId}</code>…</p>}{availableRecoveryKit?.purpose === 'fresh_start' && availableRecoveryKit.operationId === freshStartExportOperationId && <div className="backup-recovery-download"><strong>Recovery kit ready for operation <code>{availableRecoveryKit.operationId}</code></strong><span>Download this exact operation’s kit before acknowledging it.</span><div className="backup-setup-card__actions"><button className="button button--primary" type="button" onClick={() => { downloadRecoveryKit(availableRecoveryKit.kit); setDownloadedRecoveryOperationId(availableRecoveryKit.operationId); }}>Download this recovery kit</button><button className="button button--secondary" type="button" disabled={downloadedRecoveryOperationId !== availableRecoveryKit.operationId || busyAction === 'acknowledge_recovery_kit' || freshStartAcknowledgedOperationId === availableRecoveryKit.operationId} onClick={() => void acknowledgeRecoveryKit()}>{freshStartAcknowledgedOperationId === availableRecoveryKit.operationId ? 'Server proof confirmed' : busyAction === 'acknowledge_recovery_kit' ? 'Confirming…' : 'Acknowledge this operation’s download'}</button></div></div>}</div></section>
        <section className="backup-fresh-start__step"><span>2</span><div><h3>Confirm the detachment</h3><dl><div><dt>Preserved</dt><dd>Application database, media, controller wrapping key/cache, and archived generation records</dd></div><div><dt>Removed</dt><dd>Local Google OAuth, pending OAuth state, repository password, and current repository cache</dd></div><div><dt>Remote repository</dt><dd>Never deleted; it remains recoverable with the exported kit</dd></div></dl><label className="backup-field">Type START FRESH to continue<input value={freshStartConfirmation} onChange={(event) => setFreshStartConfirmation(event.target.value)} autoComplete="off" spellCheck={false} disabled={freshStartAcknowledgedOperationId !== freshStartExportOperationId || !overview?.freshStartProofAcknowledged} /></label><button className="button button--danger" type="button" disabled={freshStartConfirmation !== 'START FRESH' || freshStartAcknowledgedOperationId !== freshStartExportOperationId || !overview?.freshStartProofAcknowledged || !overview?.lifecycleActions.detach_repository_generation.available} onClick={detachRepositoryGeneration}>Confirm password & detach generation</button>{freshStartExportOperationId && freshStartAcknowledgedOperationId !== freshStartExportOperationId && <small>Download and acknowledge operation <code>{freshStartExportOperationId}</code> first.</small>}{freshStartAcknowledgedOperationId === freshStartExportOperationId && !overview?.freshStartProofAcknowledged && <small>The server has not confirmed fresh-start proof for this session and generation.</small>}</div></section>
      </div>
    </Modal>

    <Modal open={Boolean(pendingOperation)} title="Confirm your password" description={pendingOperation ? `${pendingOperation.label} changes owner-only backup controls. Confirm your current password to continue.` : undefined} width="small" onClose={closePendingOperation} footer={<><button className="button button--secondary" type="button" onClick={closePendingOperation} disabled={reauthenticating}>Cancel</button><button className="button button--primary" type="submit" form="backup-reauth-form" disabled={reauthenticating || !password}>{reauthenticating ? 'Confirming…' : 'Confirm & continue'}</button></>}>
      <form id="backup-reauth-form" className="dialog-form" onSubmit={submitReauthentication}>{reauthError && <p id="backup-reauth-error" className="form-alert" role="alert">{reauthError}</p>}<label>Current password<input type="password" autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setReauthError(''); }} aria-invalid={Boolean(reauthError)} aria-describedby={reauthError ? 'backup-reauth-error backup-reauth-note' : 'backup-reauth-note'} required autoFocus /></label><p id="backup-reauth-note" className="backup-reauth-note"><Icon name="shield" size={17} />Your password is sent only to the recent-authentication endpoint and is not stored in this page.</p></form>
    </Modal>
  </section>;
}

function BackupOperationHistoryList({ operations }: { operations: BackupOperation[] }) {
  return <ul className="backup-history-list">{operations.map((operation) => {
    const timestamp = operation.finishedAt ?? operation.startedAt ?? operation.createdAt;
    return <li key={operation.id}>
      <span className={`status-badge status-badge--${statusTone(operation.state)}`}><span className="status-badge__dot" />{operation.state}</span>
      <div>
        <strong>{operationLabel(operation.type)}</strong>
        <small>Phase: {(operation.phase ?? 'unknown').replace(/_/g, ' ')}</small>
        <p className="backup-history-list__message">{operation.message ?? 'No additional status message.'}</p>
        {timestamp ? <time dateTime={timestamp}>{formatTimestamp(timestamp)}</time> : <span className="backup-history-list__timestamp">Timestamp unavailable</span>}
      </div>
    </li>;
  })}</ul>;
}

interface ArchivedIntegrationsListProps {
  integrations: BackupArchivedIntegration[];
  disabled: boolean;
  blockedReason?: string;
  onReactivate: (integration: BackupArchivedIntegration) => void;
}

function ArchivedIntegrationsList({ integrations, disabled, blockedReason, onReactivate }: ArchivedIntegrationsListProps) {
  return <article className="backup-archived-integrations" aria-labelledby="backup-archived-integrations-title">
    <header><div><span className="eyebrow">Preserved destinations</span><h2 id="backup-archived-integrations-title">Archived backup integrations</h2><p>Archived repositories remain in their original Google Drive destinations. Reactivation reuses preserved history; it never deletes or copies a repository.</p></div><span className="status-badge status-badge--neutral"><span className="status-badge__dot" />{integrations.length}</span></header>
    {integrations.length ? <ul>{integrations.map((integration) => <li key={integration.id}>
      <div className="backup-archived-integrations__heading"><div><strong>{integration.accountEmail}</strong><span>{integration.folderDisplayName}</span></div><span className={`status-badge status-badge--${integration.reactivationAvailable ? 'ready' : 'neutral'}`}><span className="status-badge__dot" />{integration.reactivationAvailable ? 'available' : 'blocked'}</span></div>
      <dl><div><dt>Generation</dt><dd>{integration.number ?? '—'}</dd></div><div><dt>Repository</dt><dd><code>{integration.repositoryIdPrefix}</code></dd></div><div><dt>Detached</dt><dd>{formatTimestamp(integration.detachedAt)}</dd></div><div><dt>Last successful backup</dt><dd>{formatTimestamp(integration.lastSuccessfulBackupAt)}</dd></div><div><dt>Last verified backup</dt><dd>{formatTimestamp(integration.lastVerifiedBackupAt)}</dd></div><div><dt>Records</dt><dd>{integration.snapshotCount} snapshots · {integration.restoreTestCount} restore tests</dd></div></dl>
      <button className="button button--secondary" type="button" disabled={disabled || !integration.reactivationAvailable} title={disabled ? blockedReason : integration.blockedReason} onClick={() => onReactivate(integration)}>Reactivate integration</button>
      {!integration.reactivationAvailable && integration.blockedReason && <small role="status">{integration.blockedReason}</small>}
      {disabled && integration.reactivationAvailable && blockedReason && <small role="status">{blockedReason}</small>}
    </li>)}</ul> : <p className="backup-archived-integrations__empty">No recoverable archived integrations are available.</p>}
  </article>;
}

interface ScheduleFormProps {
  eyebrow?: string;
  schedule: { time: string; timezone: string };
  setSchedule: Dispatch<SetStateAction<{ time: string; timezone: string }>>;
  timezoneOptions: string[];
  busy: boolean;
  disabled: boolean;
  onSubmit: (event: FormEvent) => void;
}

function ScheduleForm({ eyebrow = 'Step 4 of 5', schedule, setSchedule, timezoneOptions, busy, disabled, onSubmit }: ScheduleFormProps) {
  return <article className="backup-policy-card"><div><span className="eyebrow">{eyebrow}</span><h2>Daily schedule</h2><p>Choose a local start time. The controller calculates and persists future runs.</p></div><form className="backup-policy-form" onSubmit={onSubmit}><fieldset disabled={disabled}><label>Frequency<select className="control-select" value="daily" disabled><option value="daily">Daily</option></select></label><label>Local start time<input type="time" value={schedule.time} onChange={(event) => setSchedule((value) => ({ ...value, time: event.target.value }))} required /></label><label>Timezone<input list="backup-timezones" value={schedule.timezone} onChange={(event) => setSchedule((value) => ({ ...value, timezone: event.target.value }))} autoComplete="off" required /><datalist id="backup-timezones">{timezoneOptions.map((timezone) => <option key={timezone} value={timezone} />)}</datalist></label><button className="button button--primary" type="submit">{busy ? 'Saving…' : 'Confirm password & save schedule'}</button></fieldset></form></article>;
}

interface RetentionFormProps {
  eyebrow?: string;
  retention: { daily: number; weekly: number; monthly: number };
  setRetention: Dispatch<SetStateAction<{ daily: number; weekly: number; monthly: number }>>;
  busy: boolean;
  disabled: boolean;
  onSubmit: (event: FormEvent) => void;
}

function RetentionForm({ eyebrow = 'Step 4 of 5', retention, setRetention, busy, disabled, onSubmit }: RetentionFormProps) {
  return (
    <article className="backup-policy-card">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2>Retention policy</h2>
        <p>Keep useful restore points without allowing unbounded values.</p>
      </div>
      <form className="backup-policy-form backup-policy-form--retention" onSubmit={onSubmit}>
        <fieldset disabled={disabled}>
          {(Object.keys(RETENTION_BOUNDS) as Array<keyof typeof RETENTION_BOUNDS>).map((key) => {
            const label = `${key[0]?.toUpperCase()}${key.slice(1)} snapshots`;
            const bounds = RETENTION_BOUNDS[key];
            const range = `${bounds.min}–${bounds.max}`;
            return (
              <label key={key}>
                {label}
                <input
                  type="number"
                  aria-label={label}
                  min={bounds.min}
                  max={bounds.max}
                  step={1}
                  value={retention[key]}
                  onChange={(event) => setRetention((value) => ({ ...value, [key]: Number(event.target.value) }))}
                  required
                />
                <small>{range}</small>
              </label>
            );
          })}
          <button className="button button--primary" type="submit">{busy ? 'Saving…' : 'Confirm password & save retention'}</button>
        </fieldset>
      </form>
    </article>
  );
}

interface ActionButtonProps {
  icon: 'archive' | 'check' | 'refresh' | 'shield' | 'upload';
  label: string;
  description: string;
  disabled: boolean;
  busy: boolean;
  primary?: boolean;
  onClick: () => void;
}

function ActionButton({ icon, label, description, disabled, busy, primary, onClick }: ActionButtonProps) {
  return <button className={`backup-action-card ${primary ? 'backup-action-card--primary' : ''}`} type="button" disabled={disabled} onClick={onClick}><span><Icon name={icon} size={21} /></span><div><strong>{busy ? 'Queueing…' : label}</strong><small>{description}</small></div><Icon name="chevron" size={16} /></button>;
}

interface BackupResourceBannerProps {
  overview: BackupOverview;
}

const PRESSURE_LABELS: Record<Exclude<NonNullable<BackupOverview['controller']['resourceGovernance']>['lastResourcePressureKind'], null>, string> = {
  cpu_throttled: 'CPU throttling observed',
  memory_pressure: 'Memory pressure observed',
  upload_throttled: 'Upload throttling observed',
  lease_heartbeat_lag: 'Heartbeat lag observed',
};

function BackupResourceBanner({ overview }: BackupResourceBannerProps) {
  const governance = overview.controller.resourceGovernance;
  const overlap = overview.scheduleOverlapWarning;
  const metrics: Array<{ label: string; value: string }> = [];
  if (governance) {
    if (governance.cpuLimitCores !== null) {
      metrics.push({ label: 'CPU limit', value: `${governance.cpuLimitCores.toFixed(2).replace(/\.?0+$/, '')} core${governance.cpuLimitCores === 1 ? '' : 's'}` });
    }
    if (governance.memoryLimitMb !== null) {
      metrics.push({ label: 'Memory limit', value: formatBytes(governance.memoryLimitMb * 1024 * 1024) });
    }
    if (governance.uploadLimitKbps !== null) {
      const kbps = governance.uploadLimitKbps;
      const text = kbps >= 1024 ? `${(kbps / 1024).toFixed(1)} MiB/s upload cap` : `${kbps} KiB/s upload cap`;
      metrics.push({ label: 'Bandwidth', value: text });
    }
    if (governance.cpuShares !== null) {
      metrics.push({ label: 'CPU shares', value: String(governance.cpuShares) });
    }
    if (governance.resticCacheDirMb !== null) {
      metrics.push({ label: 'Restic cache', value: formatBytes(governance.resticCacheDirMb * 1024 * 1024) });
    }
  }
  const pressureActive = Boolean(governance?.lastResourcePressureKind);
  const overlapActive = Boolean(overlap?.active && overlap.reason);
  return <article
    className="backup-resource-banner"
    aria-label="Backup resource governance"
  >
    <header className="backup-resource-banner__header">
      <span className="backup-resource-banner__icon"><Icon name="server" size={18} /></span>
      <div>
        <span className="eyebrow">Resource governance</span>
        <h2>{governance ? 'Controller is host-limited' : 'Controller resource limits not reported'}</h2>
        <p>
          {governance
            ? 'The Compose deployment bounds the controller so HTTP, FFmpeg, and PostgreSQL keep their CPU, memory, and bandwidth during backups.'
            : 'The controller has not declared CPU, memory, or bandwidth limits. Confirm the deployment applies resource limits before relying on this installation on a shared VPS.'}
        </p>
      </div>
    </header>
    {metrics.length > 0 && <dl className="backup-resource-banner__metrics">
      {metrics.map((metric) => <div key={metric.label}><dt>{metric.label}</dt><dd>{metric.value}</dd></div>)}
    </dl>}
    {pressureActive && governance?.lastResourcePressureAt && governance.lastResourcePressureKind && <p className="backup-resource-banner__pressure" role="status">
      <Icon name="warning" size={16} />
      <span>
        <strong>{PRESSURE_LABELS[governance.lastResourcePressureKind]}</strong>
        <small>Last observed {formatTimestamp(governance.lastResourcePressureAt)}. Backups continue but may run slower than usual.</small>
      </span>
    </p>}
    {overlapActive && overlap?.reason && <p className="backup-resource-banner__overlap" role="status">
      <Icon name="warning" size={16} />
      <span>{overlap.reason}</span>
    </p>}
  </article>;
}
